import { useEffect, useMemo, useState, type FormEvent } from "react";
import {
  AlertTriangle,
  CalendarClock,
  CircleDollarSign,
  Download,
  ImagePlus,
  LayoutGrid,
  List,
  ListFilter,
  Plus,
  Save,
  Search,
  Upload,
  X,
} from "lucide-react";
import { api, useAuth } from "./auth";
import { ReportsWorkspace as FullReportsWorkspace } from "./ReportsWorkspace";
import { Link } from "react-router-dom";

type Row = Record<string, unknown>;
type Entity = {
  id: string;
  code: string;
  trade_name: string | null;
  legal_name_en: string;
};
type Party = { id: string; display_name: string };
type Unit = {
  id: string;
  reference: string;
  unit_number: string;
  property_name: string;
};
type Property = { id: string; reference: string; title_en: string; listing_type?: string; availability_status?: string; community?: string; developer_name?: string; primary_media_id?: string };
type Vehicle = {
  id: string;
  reference: string;
  plate_number: string;
  make: string;
  model: string;
};
type Field = {
  name: string;
  label: string;
  type?:
    | "text"
    | "number"
    | "date"
    | "datetime-local"
    | "select"
    | "textarea"
    | "checkbox";
  required?: boolean;
  options?: Array<[string, string]>;
  source?: "entities" | "parties" | "units" | "properties" | "vehicles";
};
type Config = {
  title: string;
  tabLabel?: string;
  eyebrow: string;
  description: string;
  endpoint: string;
  createEndpoint: string;
  createLabel: string;
  columns: Array<[string, string]>;
  fields: Field[];
  serviceType?: string;
  mode?:
    | "invoice"
    | "unit"
    | "holiday"
    | "tenancy"
    | "service"
    | "rentalRequests"
    | "propertyAsset";
};
const emirates = [
  "Abu Dhabi",
  "Dubai",
  "Sharjah",
  "Ajman",
  "Umm Al Quwain",
  "Ras Al Khaimah",
  "Fujairah",
].map((x) => [x, x] as [string, string]);
const leadBusinessByModule: Record<string, string> = {
  "real-estate": "REAL_ESTATE",
  "business-center": "BUSINESS_CENTER",
  cars: "CAR_RENTAL",
  cleaning: "CLEANING",
  maintenance: "MAINTENANCE",
  "holiday-homes": "HOLIDAY_HOMES",
};
const configs: Record<string, Config> = {
  "real-estate": {
    title: "Real Estate",
    tabLabel: "Properties",
    eyebrow: "UAE brokerage & inventory",
    description:
      "Properties, units and listing controls for sales, leasing and portal publication.",
    endpoint: "/api/v1/real-estate/properties",
    createEndpoint: "/api/v1/real-estate/properties",
    createLabel: "Add property",
    columns: [
      ["reference", "Reference"],
      ["title_en", "Property"],
      ["property_type", "Type"],
      ["emirate", "Emirate"],
      ["community", "Community"],
      ["asking_price", "Asking price"],
      ["availability_status", "Availability"],
    ],
    fields: [
      {
        name: "legalEntityId",
        label: "Legal entity",
        type: "select",
        source: "entities",
        required: true,
      },
      {
        name: "ownerPartyId",
        label: "Owner",
        type: "select",
        source: "parties",
      },
      { name: "titleEn", label: "Property name", required: true },
      { name: "unitNumber", label: "Unit number" },
      {
        name: "category",
        label: "Category",
        type: "select",
        options: [
          ["RESIDENTIAL", "Residential"],
          ["COMMERCIAL", "Commercial"],
          ["INDUSTRIAL", "Industrial"],
          ["LAND", "Land"],
          ["MIXED_USE", "Mixed use"],
        ],
      },
      {
        name: "usageType",
        label: "Property usage",
        type: "select",
        options: [
          ["END_USE", "End use"],
          ["INVESTMENT", "Investment"],
          ["HOLIDAY_HOME", "Holiday home"],
          ["BUSINESS_CENTRE", "Business centre"],
        ],
      },
      {
        name: "listingType",
        label: "Listing type",
        type: "select",
        options: [
          ["SALE", "Sale"],
          ["RENT", "Rent"],
          ["SALE_AND_RENT", "Sale and rent"],
        ],
      },
      {
        name: "listingSource",
        label: "Listing source",
        type: "select",
        options: [
          ["DIRECT_OWNER", "Direct owner"],
          ["DEVELOPER", "Developer"],
          ["BROKER", "Broker"],
          ["INTERNAL", "Internal"],
          ["PORTAL", "Portal"],
        ],
      },
      {
        name: "propertyType",
        label: "Property type",
        type: "select",
        options: [
          ["BUILDING", "Building"],
          ["VILLA", "Villa"],
          ["APARTMENT", "Apartment"],
          ["OFFICE", "Office"],
          ["WAREHOUSE", "Warehouse"],
          ["LAND", "Land"],
        ],
        required: true,
      },
      {
        name: "ownershipType",
        label: "Ownership",
        type: "select",
        options: [
          ["CLIENT", "Client owned"],
          ["COMPANY", "Company owned"],
          ["DEVELOPER", "Developer owned"],
        ],
      },
      {
        name: "availabilityStatus",
        label: "Availability",
        type: "select",
        options: [
          ["AVAILABLE", "Available"],
          ["RESERVED", "Reserved"],
          ["UNDER_OFFER", "Under offer"],
          ["SOLD", "Sold"],
          ["RENTED", "Rented"],
          ["OFF_MARKET", "Off market"],
        ],
      },
      {
        name: "emirate",
        label: "Emirate",
        type: "select",
        options: emirates,
        required: true,
      },
      { name: "city", label: "City" },
      { name: "community", label: "Community" },
      { name: "building", label: "Building / project" },
      { name: "bedrooms", label: "Bedrooms", type: "number" },
      { name: "bathrooms", label: "Bathrooms", type: "number" },
      { name: "areaSqft", label: "Built-up area sqft", type: "number" },
      { name: "plotSqft", label: "Plot area sqft", type: "number" },
      { name: "askingPrice", label: "Asking price", type: "number" },
      {
        name: "currency",
        label: "Currency",
        type: "select",
        options: [
          ["AED", "AED"],
          ["USD", "USD"],
          ["GBP", "GBP"],
          ["EUR", "EUR"],
        ],
      },
      {
        name: "furnishedStatus",
        label: "Furnishing",
        type: "select",
        options: [
          ["UNFURNISHED", "Unfurnished"],
          ["SEMI_FURNISHED", "Semi-furnished"],
          ["FURNISHED", "Furnished"],
        ],
      },
      { name: "parkingCount", label: "Parking spaces", type: "number" },
      { name: "developerName", label: "Developer" },
      { name: "viewType", label: "View" },
      { name: "completionStatus", label: "Completion status" },
      { name: "listedPublicly", label: "Publish on website", type: "checkbox" },
      { name: "addressEn", label: "Full address", type: "textarea" },
      {
        name: "descriptionEn",
        label: "Marketing description",
        type: "textarea",
      },
    ],
  },
  "business-center": {
    title: "Central Royal Business Center",
    eyebrow: "Controlled tenant lifecycle",
    description:
      "One accountable process from deposit through Ejari, IT, office readiness and physical handover.",
    endpoint: "/api/v1/business-center/tenancies",
    createEndpoint: "/api/v1/business-center/tenancies",
    createLabel: "Start onboarding",
    mode: "tenancy",
    columns: [
      ["reference", "Reference"],
      ["tenant_name", "Tenant"],
      ["unit_number", "Office"],
      ["annual_rent", "Annual rent"],
      ["security_deposit", "Deposit"],
      ["cheque_count", "Cheques"],
      ["status", "Stage"],
    ],
    fields: [
      {
        name: "legalEntityId",
        label: "Legal entity",
        type: "select",
        source: "entities",
        required: true,
      },
      {
        name: "tenantPartyId",
        label: "Tenant",
        type: "select",
        source: "parties",
        required: true,
      },
      {
        name: "unitId",
        label: "Office / unit",
        type: "select",
        source: "units",
        required: true,
      },
      { name: "startDate", label: "Start date", type: "date", required: true },
      { name: "endDate", label: "End date", type: "date", required: true },
      {
        name: "annualRent",
        label: "Annual rent",
        type: "number",
        required: true,
      },
      {
        name: "securityDeposit",
        label: "Security deposit",
        type: "number",
        required: true,
      },
      {
        name: "paymentCount",
        label: "Rental payments",
        type: "number",
        required: true,
      },
      {
        name: "currency",
        label: "Currency",
        type: "select",
        options: [
          ["AED", "AED"],
          ["USD", "USD"],
          ["GBP", "GBP"],
          ["EUR", "EUR"],
          ["PKR", "PKR"],
          ["INR", "INR"],
        ],
        required: true,
      },
    ],
  },
  cars: {
    title: "Royal Rentacar",
    eyebrow: "Fleet command centre",
    description:
      "Vehicle availability, compliance expiries, bookings, handovers, Salik, fines and damage control.",
    endpoint: "/api/v1/fleet/vehicles",
    createEndpoint: "/api/v1/fleet/vehicles",
    createLabel: "Add vehicle",
    columns: [
      ["reference", "Reference"],
      ["plate_number", "Plate"],
      ["make", "Make"],
      ["model", "Model"],
      ["daily_rate", "Daily rate"],
      ["open_bookings", "Bookings"],
      ["status", "Status"],
    ],
    fields: [
      {
        name: "royalCarEntity",
        label: "Contracting entity",
        type: "select",
        options: [["ROYAL_CAR", "Royal Rentacar · fixed"]],
        required: true,
      },
      { name: "plateNumber", label: "Plate number", required: true },
      {
        name: "plateEmirate",
        label: "Plate emirate",
        type: "select",
        options: emirates,
        required: true,
      },
      { name: "vin", label: "VIN" },
      { name: "make", label: "Make", required: true },
      { name: "model", label: "Model", required: true },
      {
        name: "modelYear",
        label: "Model year",
        type: "number",
        required: true,
      },
      { name: "color", label: "Colour" },
      { name: "category", label: "Category" },
      {
        name: "bodyType",
        label: "Body type",
        type: "select",
        options: [
          ["SEDAN", "Sedan"],
          ["SUV", "SUV"],
          ["COUPE", "Coupe"],
          ["CONVERTIBLE", "Convertible"],
          ["HATCHBACK", "Hatchback"],
          ["VAN", "Van"],
          ["PICKUP", "Pickup"],
          ["LUXURY", "Luxury / exotic"],
        ],
      },
      { name: "trimLevel", label: "Trim / specification" },
      { name: "engineNumber", label: "Engine number" },
      { name: "engineSize", label: "Engine size" },
      {
        name: "driveType",
        label: "Drive type",
        type: "select",
        options: [
          ["FWD", "Front-wheel drive"],
          ["RWD", "Rear-wheel drive"],
          ["AWD", "All-wheel drive"],
          ["4WD", "Four-wheel drive"],
        ],
      },
      { name: "keysCount", label: "Keys held", type: "number" },
      {
        name: "ownershipStatus",
        label: "Ownership",
        type: "select",
        options: [
          ["OWNED", "Owned by Royal Rentacar"],
          ["FINANCED", "Financed by Royal Rentacar"],
          ["LEASED", "Leased by Royal Rentacar"],
        ],
      },
      { name: "acquisitionDate", label: "Acquisition date", type: "date" },
      { name: "warrantyExpiry", label: "Warranty expiry", type: "date" },
      {
        name: "transmission",
        label: "Transmission",
        type: "select",
        options: [
          ["AUTOMATIC", "Automatic"],
          ["MANUAL", "Manual"],
        ],
      },
      {
        name: "fuelType",
        label: "Fuel",
        type: "select",
        options: [
          ["PETROL", "Petrol"],
          ["DIESEL", "Diesel"],
          ["HYBRID", "Hybrid"],
          ["ELECTRIC", "Electric"],
        ],
      },
      { name: "seats", label: "Seats", type: "number" },
      { name: "doors", label: "Doors", type: "number" },
      { name: "odometerKm", label: "Odometer km", type: "number" },
      { name: "dailyRate", label: "Daily rate", type: "number" },
      { name: "weeklyRate", label: "Weekly rate", type: "number" },
      { name: "monthlyRate", label: "Monthly rate", type: "number" },
      { name: "depositAmount", label: "Security deposit", type: "number" },
      {
        name: "registrationExpiry",
        label: "Registration expiry",
        type: "date",
      },
      { name: "insuranceExpiry", label: "Insurance expiry", type: "date" },
      { name: "listedPublicly", label: "Publish on website", type: "checkbox" },
      { name: "description", label: "Vehicle description", type: "textarea" },
    ],
  },
  cleaning: {
    title: "Real Clean",
    eyebrow: "Cleaning operations",
    description:
      "Quotes, recurring plans, workforce scheduling, inspections and quality-controlled completion.",
    endpoint: "/api/v1/services/orders?type=CLEANING",
    createEndpoint: "/api/v1/services/orders",
    createLabel: "New cleaning order",
    serviceType: "CLEANING",
    columns: [
      ["reference", "Order"],
      ["customer_name", "Customer"],
      ["pricing_model", "Pricing"],
      ["start_at", "Scheduled"],
      ["assigned_team_name", "Team"],
      ["unit_price", "Value"],
      ["status", "Status"],
    ],
    fields: serviceFields("CLEANING"),
  },
  maintenance: {
    title: "I Maintain",
    eyebrow: "Technical services",
    description:
      "Service requests, quotations, work orders, dispatch, materials, SLAs and warranties.",
    endpoint: "/api/v1/services/orders?type=MAINTENANCE",
    createEndpoint: "/api/v1/services/orders",
    createLabel: "New work order",
    serviceType: "MAINTENANCE",
    columns: [
      ["reference", "Work order"],
      ["customer_name", "Customer"],
      ["start_at", "Scheduled"],
      ["sla_due_at", "SLA"],
      ["assigned_user_name", "Technician"],
      ["unit_price", "Value"],
      ["status", "Status"],
    ],
    fields: serviceFields("MAINTENANCE"),
  },
  "holiday-homes": {
    title: "Royal Maison Holiday Homes",
    eyebrow: "Guest & occupancy operations",
    description:
      "Channel bookings, guest compliance, availability, fees, check-in and housekeeping.",
    endpoint: "/api/v1/holiday-homes/bookings",
    createEndpoint: "/api/v1/holiday-homes/bookings",
    createLabel: "New booking",
    columns: [
      ["reference", "Booking"],
      ["guest_name", "Guest"],
      ["property_name", "Property"],
      ["unit_number", "Unit"],
      ["channel", "Channel"],
      ["check_in", "Check in"],
      ["check_out", "Check out"],
      ["status", "Status"],
    ],
    fields: [
      {
        name: "legalEntityId",
        label: "Legal entity",
        type: "select",
        source: "entities",
        required: true,
      },
      {
        name: "guestPartyId",
        label: "Guest",
        type: "select",
        source: "parties",
        required: true,
      },
      {
        name: "unitId",
        label: "Holiday home",
        type: "select",
        source: "units",
        required: true,
      },
      {
        name: "channel",
        label: "Booking channel",
        type: "select",
        options: [
          ["DIRECT", "Direct"],
          ["AIRBNB", "Airbnb"],
          ["BOOKING_COM", "Booking.com"],
          ["EXPEDIA", "Expedia"],
          ["OTHER", "Other"],
        ],
        required: true,
      },
      { name: "checkIn", label: "Check in", type: "date", required: true },
      { name: "checkOut", label: "Check out", type: "date", required: true },
      { name: "adults", label: "Adults", type: "number", required: true },
      { name: "children", label: "Children", type: "number" },
      {
        name: "accommodationAmount",
        label: "Accommodation",
        type: "number",
        required: true,
      },
      { name: "cleaningAmount", label: "Cleaning fee", type: "number" },
      { name: "tourismFee", label: "Tourism fee", type: "number" },
      { name: "vatAmount", label: "VAT", type: "number" },
    ],
  },
  finance: {
    title: "Group Finance",
    tabLabel: "Invoices",
    eyebrow: "Multi-entity accounting",
    description:
      "Entity ledgers, UAE VAT, invoices, receipts, payments, PDC custody and consolidation controls.",
    endpoint: "/api/v1/finance/invoices",
    createEndpoint: "/api/v1/finance/invoices",
    createLabel: "Issue invoice",
    mode: "invoice",
    columns: [
      ["invoice_number", "Invoice"],
      ["trade_name", "Entity"],
      ["customer_name", "Customer"],
      ["issue_date", "Issue date"],
      ["due_date", "Due date"],
      ["total", "Total"],
      ["balance_due", "Balance"],
      ["status", "Status"],
    ],
    fields: [
      {
        name: "legalEntityId",
        label: "Legal entity",
        type: "select",
        source: "entities",
        required: true,
      },
      {
        name: "partyId",
        label: "Customer",
        type: "select",
        source: "parties",
        required: true,
      },
      { name: "issueDate", label: "Issue date", type: "date", required: true },
      { name: "dueDate", label: "Due date", type: "date", required: true },
      { name: "description", label: "Line description", required: true },
      { name: "quantity", label: "Quantity", type: "number", required: true },
      {
        name: "unitPrice",
        label: "Unit price",
        type: "number",
        required: true,
      },
      { name: "discountAmount", label: "Discount", type: "number" },
      { name: "vatRate", label: "VAT rate %", type: "number", required: true },
      {
        name: "currency",
        label: "Currency",
        type: "select",
        options: [
          ["AED", "AED"],
          ["USD", "USD"],
          ["GBP", "GBP"],
          ["EUR", "EUR"],
          ["PKR", "PKR"],
          ["INR", "INR"],
        ],
        required: true,
      },
    ],
  },
};
const extraConfigs: Record<string, Config> = {
  centralSpaces: {
    title: "Central Royal Business Center",
    tabLabel: "Offices & rooms",
    eyebrow: "Bookable workspace inventory",
    description:
      "Add every office, room and desk with pricing, photographs, availability and public-site controls.",
    endpoint: "/api/v1/real-estate/properties?businessModule=BUSINESS_CENTER",
    createEndpoint: "/api/v1/real-estate/properties",
    createLabel: "Add office or room",
    mode: "propertyAsset",
    columns: [
      ["reference", "Reference"],
      ["title_en", "Space"],
      ["property_type", "Type"],
      ["building", "Building"],
      ["area_sqft", "Area sqft"],
      ["asking_price", "Price"],
      ["availability_status", "Availability"],
    ],
    fields: [
      {
        name: "businessModule",
        label: "Business",
        type: "select",
        options: [["BUSINESS_CENTER", "Central Royal"]],
        required: true,
      },
      ...configs["real-estate"].fields,
    ],
  },
  holidaySpaces: {
    title: "Royal Maison Holiday Homes",
    tabLabel: "Homes & units",
    eyebrow: "Guest-ready inventory",
    description:
      "Manage holiday-home units, rates, photographs, availability and public publication before taking bookings.",
    endpoint: "/api/v1/real-estate/properties?businessModule=HOLIDAY_HOMES",
    createEndpoint: "/api/v1/real-estate/properties",
    createLabel: "Add holiday home",
    mode: "propertyAsset",
    columns: [
      ["reference", "Reference"],
      ["title_en", "Holiday home"],
      ["property_type", "Type"],
      ["community", "Community"],
      ["bedrooms", "Bedrooms"],
      ["asking_price", "Nightly guide"],
      ["availability_status", "Availability"],
    ],
    fields: [
      {
        name: "businessModule",
        label: "Business",
        type: "select",
        options: [["HOLIDAY_HOMES", "Royal Maison Holiday Homes"]],
        required: true,
      },
      ...configs["real-estate"].fields,
    ],
  },
  units: {
    title: "Real Estate",
    tabLabel: "Units",
    eyebrow: "UAE brokerage & inventory",
    description: configs["real-estate"].description,
    endpoint: "/api/v1/real-estate/units",
    createEndpoint: "/api/v1/real-estate/properties/{propertyId}/units",
    createLabel: "Add unit",
    mode: "unit",
    columns: [
      ["reference", "Reference"],
      ["property_name", "Property"],
      ["unit_number", "Unit"],
      ["unit_type", "Type"],
      ["floor", "Floor"],
      ["area_sqft", "Area sqft"],
      ["occupancy_status", "Occupancy"],
    ],
    fields: [
      {
        name: "propertyId",
        label: "Property",
        type: "select",
        source: "properties",
        required: true,
      },
      { name: "unitNumber", label: "Unit / office number", required: true },
      {
        name: "unitType",
        label: "Unit type",
        type: "select",
        options: [
          ["APARTMENT", "Apartment"],
          ["VILLA", "Villa"],
          ["OFFICE", "Office"],
          ["SHOP", "Shop"],
          ["WAREHOUSE", "Warehouse"],
          ["DESK", "Desk"],
        ],
        required: true,
      },
      { name: "floor", label: "Floor" },
      { name: "bedrooms", label: "Bedrooms", type: "number" },
      { name: "bathrooms", label: "Bathrooms", type: "number" },
      { name: "areaSqft", label: "Area sqft", type: "number" },
      { name: "parkingCount", label: "Parking spaces", type: "number" },
      {
        name: "furnishedStatus",
        label: "Furnishing",
        type: "select",
        options: [
          ["UNFURNISHED", "Unfurnished"],
          ["SEMI_FURNISHED", "Semi-furnished"],
          ["FURNISHED", "Furnished"],
        ],
      },
    ],
  },
  listings: {
    title: "Real Estate",
    tabLabel: "Listings",
    eyebrow: "UAE brokerage & inventory",
    description: configs["real-estate"].description,
    endpoint: "/api/v1/real-estate/listings",
    createEndpoint: "/api/v1/real-estate/listings",
    createLabel: "Create listing",
    columns: [
      ["reference", "Reference"],
      ["property_name", "Property"],
      ["unit_number", "Unit"],
      ["transaction_type", "For"],
      ["price", "Price"],
      ["permit_number", "Permit"],
      ["status", "Status"],
    ],
    fields: [
      {
        name: "legalEntityId",
        label: "Legal entity",
        type: "select",
        source: "entities",
        required: true,
      },
      {
        name: "unitId",
        label: "Unit",
        type: "select",
        source: "units",
        required: true,
      },
      {
        name: "transactionType",
        label: "Transaction",
        type: "select",
        options: [
          ["SALE", "Sale"],
          ["RENT", "Rent"],
          ["SHORT_TERM", "Short term"],
        ],
        required: true,
      },
      { name: "price", label: "Price", type: "number", required: true },
      {
        name: "currency",
        label: "Currency",
        type: "select",
        options: [
          ["AED", "AED"],
          ["USD", "USD"],
          ["GBP", "GBP"],
          ["EUR", "EUR"],
        ],
        required: true,
      },
      { name: "rentFrequency", label: "Rent frequency" },
      { name: "titleEn", label: "Listing title" },
      { name: "permitNumber", label: "Permit number" },
      { name: "permitExpiry", label: "Permit expiry", type: "date" },
      { name: "descriptionEn", label: "Description", type: "textarea" },
    ],
  },
  fleetBookings: {
    title: "Royal Rentacar",
    tabLabel: "Bookings",
    eyebrow: "Fleet command centre",
    description: configs.cars.description,
    endpoint: "/api/v1/fleet/bookings",
    createEndpoint: "/api/v1/fleet/bookings",
    createLabel: "New booking",
    columns: [
      ["reference", "Booking"],
      ["customer_name", "Customer"],
      ["plate_number", "Plate"],
      ["make", "Make"],
      ["model", "Model"],
      ["pickup_at", "Pickup"],
      ["return_at", "Return"],
      ["base_amount", "Rental"],
      ["status", "Status"],
    ],
    fields: [
      {
        name: "vehicleId",
        label: "Vehicle",
        type: "select",
        source: "vehicles",
        required: true,
      },
      {
        name: "customerPartyId",
        label: "Customer",
        type: "select",
        source: "parties",
        required: true,
      },
      {
        name: "rentalType",
        label: "Rental type",
        type: "select",
        options: [
          ["SELF_DRIVE", "Self drive"],
          ["CHAUFFEUR", "Chauffeur"],
        ],
        required: true,
      },
      {
        name: "pickupAt",
        label: "Pickup",
        type: "datetime-local",
        required: true,
      },
      {
        name: "returnAt",
        label: "Return",
        type: "datetime-local",
        required: true,
      },
      { name: "pickupLocation", label: "Pickup location" },
      { name: "returnLocation", label: "Return location" },
      { name: "ratePlan", label: "Rate plan" },
      {
        name: "baseAmount",
        label: "Rental amount",
        type: "number",
        required: true,
      },
      { name: "depositAmount", label: "Security deposit", type: "number" },
      { name: "driverName", label: "Primary driver full name" },
      { name: "driverMobile", label: "Driver mobile" },
      { name: "licenceNumber", label: "Driving licence number" },
      { name: "licenceCountry", label: "Licence country code" },
      { name: "licenceExpiry", label: "Licence expiry", type: "date" },
      { name: "discountAmount", label: "Discount amount", type: "number" },
      { name: "promoCode", label: "Promo code" },
      {
        name: "currency",
        label: "Currency",
        type: "select",
        options: [
          ["AED", "AED"],
          ["USD", "USD"],
          ["GBP", "GBP"],
          ["EUR", "EUR"],
        ],
        required: true,
      },
    ],
  },
  rentalRequests: {
    title: "Royal Rentacar",
    tabLabel: "Rental requests",
    eyebrow: "Customer rental demand",
    description:
      "Website rental enquiries with requested dates, delivery, chauffeur, driver and vehicle preference details.",
    endpoint: "/api/v1/enterprise/rentals/requests",
    createEndpoint: "",
    createLabel: "",
    mode: "rentalRequests",
    columns: [
      ["reference", "Request"],
      ["customer_name", "Customer"],
      ["mobile", "Contact"],
      ["make", "Vehicle"],
      ["pickup_at", "Pickup"],
      ["return_at", "Return"],
      ["pickup_location", "Location"],
      ["status", "Status"],
    ],
    fields: [],
  },
  payments: {
    title: "Group Finance",
    tabLabel: "Payments",
    eyebrow: "Multi-entity accounting",
    description: configs.finance.description,
    endpoint: "/api/v1/finance/payments",
    createEndpoint: "/api/v1/finance/payments",
    createLabel: "Record payment",
    columns: [
      ["reference", "Reference"],
      ["trade_name", "Entity"],
      ["party_name", "Party"],
      ["direction", "Direction"],
      ["method", "Method"],
      ["payment_date", "Date"],
      ["amount", "Amount"],
      ["status", "Status"],
    ],
    fields: [
      {
        name: "legalEntityId",
        label: "Legal entity",
        type: "select",
        source: "entities",
        required: true,
      },
      { name: "partyId", label: "Party", type: "select", source: "parties" },
      {
        name: "direction",
        label: "Direction",
        type: "select",
        options: [
          ["RECEIPT", "Receipt"],
          ["PAYMENT", "Payment"],
        ],
        required: true,
      },
      {
        name: "method",
        label: "Method",
        type: "select",
        options: [
          ["CASH", "Cash"],
          ["CHEQUE", "Cheque"],
          ["BANK_TRANSFER", "Bank transfer"],
          ["CARD", "Card"],
          ["PAYMENT_LINK", "Payment link"],
          ["WALLET", "Wallet"],
          ["OTHER", "Other"],
        ],
        required: true,
      },
      {
        name: "paymentDate",
        label: "Payment date",
        type: "date",
        required: true,
      },
      { name: "amount", label: "Amount", type: "number", required: true },
      {
        name: "currency",
        label: "Currency",
        type: "select",
        options: [
          ["AED", "AED"],
          ["USD", "USD"],
          ["GBP", "GBP"],
          ["EUR", "EUR"],
          ["PKR", "PKR"],
          ["INR", "INR"],
        ],
        required: true,
      },
      { name: "bankReference", label: "Bank / transaction reference" },
    ],
  },
  cheques: {
    title: "Group Finance",
    tabLabel: "PDC & cheques",
    eyebrow: "Multi-entity accounting",
    description: configs.finance.description,
    endpoint: "/api/v1/finance/cheques",
    createEndpoint: "/api/v1/finance/cheques",
    createLabel: "Register cheque",
    columns: [
      ["cheque_number", "Cheque"],
      ["trade_name", "Entity"],
      ["party_name", "Party"],
      ["direction", "Direction"],
      ["bank_name", "Bank"],
      ["cheque_date", "Due date"],
      ["amount", "Amount"],
      ["status", "Status"],
    ],
    fields: [
      {
        name: "legalEntityId",
        label: "Legal entity",
        type: "select",
        source: "entities",
        required: true,
      },
      { name: "partyId", label: "Party", type: "select", source: "parties" },
      {
        name: "direction",
        label: "Direction",
        type: "select",
        options: [
          ["RECEIVED", "Received"],
          ["ISSUED", "Issued"],
        ],
        required: true,
      },
      { name: "bankName", label: "Bank name", required: true },
      { name: "chequeNumber", label: "Cheque number", required: true },
      {
        name: "chequeDate",
        label: "Cheque date",
        type: "date",
        required: true,
      },
      { name: "amount", label: "Amount", type: "number", required: true },
      {
        name: "currency",
        label: "Currency",
        type: "select",
        options: [
          ["AED", "AED"],
          ["USD", "USD"],
          ["GBP", "GBP"],
          ["EUR", "EUR"],
        ],
        required: true,
      },
      { name: "custodyLocation", label: "Custody location" },
    ],
  },
};
const moduleViews: Record<string, Config[]> = {
  "real-estate": [
    configs["real-estate"],
    extraConfigs.listings,
  ],
  "business-center": [extraConfigs.centralSpaces, configs["business-center"]],
  "holiday-homes": [extraConfigs.holidaySpaces, configs["holiday-homes"]],
  cars: [configs.cars, extraConfigs.fleetBookings, extraConfigs.rentalRequests],
  finance: [configs.finance, extraConfigs.payments, extraConfigs.cheques],
};
function serviceFields(_type: string): Field[] {
  return [
    {
      name: "legalEntityId",
      label: "Legal entity",
      type: "select",
      source: "entities",
      required: true,
    },
    { name: "partyId", label: "Customer", type: "select", source: "parties" },
    { name: "unitId", label: "Unit / office", type: "select", source: "units" },
    {
      name: "pricingModel",
      label: "Pricing model",
      type: "select",
      options: [
        ["HOURLY", "Hourly"],
        ["PER_VISIT", "Per visit"],
        ["AREA", "Area based"],
        ["PACKAGE", "Package"],
        ["SUBSCRIPTION", "Subscription"],
        ["CUSTOM", "Custom quote"],
      ],
      required: true,
    },
    { name: "quantity", label: "Quantity", type: "number", required: true },
    { name: "unitPrice", label: "Unit price", type: "number", required: true },
    { name: "startAt", label: "Scheduled start", type: "datetime-local" },
    { name: "endAt", label: "Scheduled end", type: "datetime-local" },
    {
      name: "recurrence",
      label: "Recurrence",
      type: "select",
      options: [
        ["", "One time"],
        ["WEEKLY", "Weekly"],
        ["MONTHLY", "Monthly"],
        ["CUSTOM", "Custom"],
      ],
    },
  ];
}

export function OperationsWorkspace({ moduleKey }: { moduleKey: string }) {
  const views = moduleViews[moduleKey] || [configs[moduleKey]].filter(Boolean);
  const [viewIndex, setViewIndex] = useState(0);
  const config = views[viewIndex];
  const { csrfToken, user } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState<Row | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [propertyView, setPropertyView] = useState<"TABLE" | "CARDS">("TABLE");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [propertyFilters, setPropertyFilters] = useState({ listingType: "ALL", emirate: "ALL", community: "ALL", developer: "ALL", availability: "ALL" });
  const [propertyMaster, setPropertyMaster] = useState<{areas: Row[]; developers: Row[]}>({ areas: [], developers: [] });
  const elevated = ["SUPERADMIN", "GROUP_ADMIN", "COMPANY_ADMIN", "COO", "OWNER"].includes(String(user?.role));
  const can = (code: string) => elevated || Boolean(user?.permissions?.includes(code));
  async function load() {
    if (!config) return;
    setLoading(true);
    setError("");
    try {
      const result = await api<{ data: Row[] }>(config.endpoint);
      setRows(result.data);
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to load records",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    setViewIndex(0);
  }, [moduleKey]);
  useEffect(() => {
    void load();
  }, [config?.endpoint]);
  useEffect(() => {
    if (moduleKey !== "real-estate") return;
    api<{data:{areas:Row[];developers:Row[]}}>("/api/v1/real-estate/master-data").then((result) => setPropertyMaster(result.data)).catch(() => {});
  }, [moduleKey]);
  async function downloadPropertyTemplate() {
    setError("");
    const response = await fetch("/api/v1/real-estate/properties/import-template", { credentials: "include" });
    if (!response.ok) throw new Error("Property template download failed. Check your access right and try again.");
    const blob = await response.blob(), link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "realco-property-import-template.xlsx";
    document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(link.href);
  }
  async function importProperties(file: File) {
    if (!csrfToken) return;
    setImportBusy(true); setError("");
    try {
      const response = await fetch("/api/v1/real-estate/properties/import", { method: "POST", credentials: "include", headers: { "content-type": file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", "x-csrf-token": csrfToken }, body: file });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || body.code || "Property import failed");
      const errors = body.data?.errors?.length ? ` ${body.data.errors.length} row issue(s) are available in the response.` : "";
      setError(`Imported ${body.data?.created || 0}; skipped ${body.data?.skipped || 0}.${errors}`);
      await load();
    } catch (value) { setError(value instanceof Error ? value.message : "Property import failed"); }
    finally { setImportBusy(false); }
  }
  const filtered = useMemo(() => rows.filter((row) => {
    const textMatch = Object.values(row).some((value) => String(value ?? "").toLowerCase().includes(query.toLowerCase()));
    if (!textMatch || moduleKey !== "real-estate" || config !== configs["real-estate"]) return textMatch;
    const listing = String(row.listing_type || "SALE");
    return (propertyFilters.listingType === "ALL" || listing === propertyFilters.listingType || listing === "SALE_AND_RENT")
      && (propertyFilters.emirate === "ALL" || row.emirate === propertyFilters.emirate)
      && (propertyFilters.community === "ALL" || row.community === propertyFilters.community)
      && (propertyFilters.developer === "ALL" || row.developer_name === propertyFilters.developer)
      && (propertyFilters.availability === "ALL" || row.availability_status === propertyFilters.availability);
  }), [rows, query, moduleKey, config, propertyFilters]);
  const statusCount = (name: string) =>
    rows.filter((row) => String(row.status || "").includes(name)).length;
  const total = rows.reduce(
    (sum, row) =>
      sum +
      Number(
        row.total ||
          row.unit_price ||
          row.annual_rent ||
          row.price ||
          row.base_amount ||
          row.amount ||
          row.accommodation_amount ||
          0,
      ),
    0,
  );
  if (!config) return <FullReportsWorkspace />;
  return (
    <div className="content">
      <section className="page-heading">
        <div>
          <span className="kicker">{config.eyebrow}</span>
          <h1>{config.title}</h1>
          <p>{config.description}</p>
        </div>
        <div className="heading-actions">
          {moduleKey === "real-estate" && config === configs["real-estate"] && can("PROPERTIES_IMPORT") && (
            <>
              <button className="button secondary" onClick={() => void downloadPropertyTemplate()}><Download size={17}/>Download Excel template</button>
              <label className={`button secondary file-button ${importBusy ? "disabled" : ""}`}><Upload size={17}/>{importBusy ? "Uploading…" : "Upload properties"}<input type="file" accept=".xlsx,.xls" disabled={importBusy} onChange={(event) => { const file = event.target.files?.[0]; if (file) void importProperties(file); event.currentTarget.value = ""; }}/></label>
            </>
          )}
          {leadBusinessByModule[moduleKey] && (
            <Link
              className="button secondary"
              to={`/modules/crm?business=${leadBusinessByModule[moduleKey]}`}
            >
              Lead pipeline
            </Link>
          )}
          {config.mode !== "rentalRequests" && (config !== configs["real-estate"] || can("PROPERTIES_CREATE")) && (
            <button className="button primary" onClick={() => setOpen(true)}>
              <Plus size={17} />
              {config.createLabel}
            </button>
          )}
        </div>
      </section>
      {views.length > 1 && (
        <div className="operations-tabs">
          {views.map((view, index) => (
            <button
              key={view.tabLabel}
              className={index === viewIndex ? "active" : ""}
              onClick={() => setViewIndex(index)}
            >
              {view.tabLabel}
            </button>
          ))}
        </div>
      )}
      <section className="metrics">
        <article>
          <span>Total records</span>
          <strong>{rows.length}</strong>
          <small>Current live register</small>
        </article>
        <article>
          <span>Active / in progress</span>
          <strong>
            {statusCount("ACTIVE") +
              statusCount("IN_PROGRESS") +
              statusCount("TENANT")}
          </strong>
          <small>Operational now</small>
        </article>
        <article>
          <span>Needs attention</span>
          <strong>{statusCount("PENDING") + statusCount("DRAFT")}</strong>
          <small>Action or approval required</small>
        </article>
        <article>
          <span>Registered value</span>
          <strong>
            {new Intl.NumberFormat("en-AE", {
              style: "currency",
              currency: "AED",
              maximumFractionDigits: 0,
            }).format(total)}
          </strong>
          <small>Operational register value</small>
        </article>
      </section>
      <section className="panel table-panel">
        <header>
          <div>
            <span className="kicker">Live register</span>
            <h2>{config.tabLabel || config.title} records</h2>
          </div>
          <div className="crm-filters">
            <label>
              <Search size={15} />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search current records"
              />
            </label>
            <button className={`button secondary ${filtersOpen ? "active" : ""}`} onClick={() => setFiltersOpen((value) => !value)}>
              <ListFilter size={15} />
              Filters
            </button>
            {moduleKey === "real-estate" && config === configs["real-estate"] && <div className="view-switch" aria-label="Property view"><button className={propertyView === "TABLE" ? "active" : ""} onClick={() => setPropertyView("TABLE")} title="Table view"><List size={16}/></button><button className={propertyView === "CARDS" ? "active" : ""} onClick={() => setPropertyView("CARDS")} title="Card view"><LayoutGrid size={16}/></button></div>}
          </div>
        </header>
        {filtersOpen && moduleKey === "real-estate" && config === configs["real-estate"] && <div className="property-filter-panel">
          <label>Purpose<select value={propertyFilters.listingType} onChange={(e) => setPropertyFilters({...propertyFilters, listingType:e.target.value})}><option value="ALL">Sale and rent</option><option value="SALE">For sale</option><option value="RENT">For rent</option><option value="SALE_AND_RENT">Sale or rent</option></select></label>
          <label>Emirate<select value={propertyFilters.emirate} onChange={(e) => setPropertyFilters({...propertyFilters, emirate:e.target.value, community:"ALL"})}><option value="ALL">All emirates</option>{emirates.map(([value,label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Area / community<select value={propertyFilters.community} onChange={(e) => setPropertyFilters({...propertyFilters, community:e.target.value})}><option value="ALL">All areas</option>{propertyMaster.areas.filter((x) => propertyFilters.emirate === "ALL" || x.emirate === propertyFilters.emirate).map((x) => <option key={String(x.id)} value={String(x.community)}>{String(x.area)} · {String(x.community)}</option>)}</select></label>
          <label>Developer<select value={propertyFilters.developer} onChange={(e) => setPropertyFilters({...propertyFilters, developer:e.target.value})}><option value="ALL">All developers</option>{propertyMaster.developers.map((x) => <option key={String(x.id)} value={String(x.name)}>{String(x.name)}</option>)}</select></label>
          <label>Status<select value={propertyFilters.availability} onChange={(e) => setPropertyFilters({...propertyFilters, availability:e.target.value})}><option value="ALL">All statuses</option>{["AVAILABLE","RESERVED","UNDER_OFFER","SOLD","RENTED","OFF_MARKET"].map((value) => <option key={value}>{value}</option>)}</select></label>
          <button className="button secondary" onClick={() => setPropertyFilters({listingType:"ALL",emirate:"ALL",community:"ALL",developer:"ALL",availability:"ALL"})}>Clear filters</button>
        </div>}
        {error && <div className="form-error crm-error">{error}</div>}
        {propertyView === "TABLE" || moduleKey !== "real-estate" || config !== configs["real-estate"] ? <div className="table-wrap">
          <table>
            <thead>
              <tr>
                {config.columns.map(([, label]) => (
                  <th key={label}>{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, index) => (
                <tr
                  className="clickable-row"
                  onClick={() => setSelected(row)}
                  key={String(row.id ?? index)}
                >
                  {config.columns.map(([key]) => (
                    <td key={key}>{renderCell(key, row[key], row)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div className="empty">Loading live records…</div>}
          {!loading && !filtered.length && (
            <div className="empty">No records match this view.</div>
          )}
        </div> : <div className="property-card-grid">{filtered.map((row, index) => <button className="property-listing-card" onClick={() => setSelected(row)} key={String(row.id ?? index)}><div className="property-cover">{row.primary_media_id ? <img src={`/api/v1/media/${row.primary_media_id}/content`} alt={String(row.title_en)}/> : <Building2Placeholder/>}<span>{String(row.listing_type || "SALE").replaceAll("_"," / ")}</span></div><div className="property-card-copy"><small>{String(row.reference)}</small><h3>{String(row.title_en)}</h3><p>{String(row.community || row.emirate || "Location not recorded")}</p><strong>{new Intl.NumberFormat("en-AE",{style:"currency",currency:String(row.currency||"AED"),maximumFractionDigits:0}).format(Number(row.asking_price||0))}</strong><footer><span>{String(row.property_type||"Property").replaceAll("_"," ")}</span><b>{String(row.availability_status||"AVAILABLE").replaceAll("_"," ")}</b></footer></div></button>)}</div>}
      </section>
      <CreateModal
        config={config}
        open={open}
        onClose={() => setOpen(false)}
        csrfToken={csrfToken}
        onCreated={() => {
          setOpen(false);
          void load();
        }}
      />
      <StatusDrawer
        row={selected}
        config={config}
        onClose={() => setSelected(null)}
        csrfToken={csrfToken}
        onChanged={() => {
          setSelected(null);
          void load();
        }}
        canEditProperty={can("PROPERTIES_EDIT")}
        canManagePropertyMedia={can("PROPERTIES_MEDIA")}
        canCommentProperty={can("PROPERTY_COMMENT") || ["AGENT", "TEAM_LEAD"].includes(String(user?.role))}
        canPublishProperty={can("PROPERTY_PORTAL_PUBLISH")}
      />
    </div>
  );
}

function Building2Placeholder(){return <div className="property-cover-placeholder"><ImagePlus/><span>Add main picture</span></div>}

function renderCell(key: string, value: unknown, row: Row) {
  if (key === "title_en") return <div className="property-table-title">{row.primary_media_id ? <img src={`/api/v1/media/${row.primary_media_id}/content`} alt=""/> : <span><ImagePlus/></span>}<div><strong>{String(value || "Untitled property")}</strong><small>{String(row.listing_type || "SALE").replaceAll("_"," / ")}</small></div></div>;
  if (["status", "availability_status"].includes(key))
    return (
      <span className="status">
        {String(value ?? "—").replaceAll("_", " ")}
      </span>
    );
  if (
    [
      "total",
      "balance_due",
      "annual_rent",
      "security_deposit",
      "unit_price",
      "asking_price",
      "daily_rate",
    ].includes(key) &&
    value != null
  )
    return new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: String(row.currency || "AED"),
      maximumFractionDigits: 0,
    }).format(Number(value));
  if (
    (key.endsWith("_at") ||
      key.endsWith("_date") ||
      key.endsWith("_expiry") ||
      key === "check_in" ||
      key === "check_out") &&
    value
  )
    return new Intl.DateTimeFormat("en-AE", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(String(value)));
  return String(value ?? "—");
}

function CreateModal({
  config,
  open,
  onClose,
  csrfToken,
  onCreated,
}: {
  config: Config;
  open: boolean;
  onClose: () => void;
  csrfToken: string | null;
  onCreated: () => void;
}) {
  const [entities, setEntities] = useState<Entity[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [properties, setProperties] = useState<Property[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [propertyMaster, setPropertyMaster] = useState<{areas: Row[]; developers: Row[]}>({ areas: [], developers: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const supportsImage =
    config === configs["real-estate"] ||
    config === configs.cars ||
    config.mode === "propertyAsset";
  useEffect(() => {
    if (!open) return;
    void Promise.all([
      api<{ data: Entity[] }>("/api/v1/master/legal-entities"),
      api<{ data: Party[] }>("/api/v1/crm/parties"),
      api<{ data: Unit[] }>("/api/v1/real-estate/units"),
      api<{ data: Property[] }>("/api/v1/real-estate/properties"),
      api<{ data: Vehicle[] }>("/api/v1/fleet/vehicles"),
      api<{data:{areas:Row[];developers:Row[]}}>("/api/v1/real-estate/master-data"),
    ])
      .then(([a, b, c, d, e, f]) => {
        setEntities(a.data);
        setParties(b.data);
        setUnits(c.data);
        setProperties(d.data);
        setVehicles(e.data);
        setPropertyMaster(f.data);
      })
      .catch(() => {});
  }, [open]);
  if (!open) return null;
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const form = new FormData(event.currentTarget);
    const payload: Record<string, unknown> = {};
    for (const field of config.fields) {
      const value = form.get(field.name);
      if (field.type === "checkbox") payload[field.name] = value === "on";
      else if (value === "") payload[field.name] = null;
      else if (field.type === "number") payload[field.name] = Number(value);
      else if (field.type === "datetime-local")
        payload[field.name] = value
          ? new Date(String(value)).toISOString()
          : null;
      else payload[field.name] = value;
    }
    if (config.serviceType) payload.serviceType = config.serviceType;
    if (config.mode === "invoice") {
      payload.lines = [
        {
          description: payload.description,
          quantity: payload.quantity,
          unitPrice: payload.unitPrice,
          discountAmount: payload.discountAmount || 0,
          vatRate: payload.vatRate,
        },
      ];
      for (const key of [
        "description",
        "quantity",
        "unitPrice",
        "discountAmount",
        "vatRate",
      ])
        delete payload[key];
      payload.invoiceType = "TAX_INVOICE";
      payload.exchangeRate = 1;
    }
    if (config.mode === "holiday") {
      payload.children = payload.children || 0;
      payload.cleaningAmount = payload.cleaningAmount || 0;
      payload.tourismFee = payload.tourismFee || 0;
      payload.vatAmount = payload.vatAmount || 0;
      payload.currency = "AED";
    }
    if (config.mode === "tenancy") {
      payload.tenancyType = "BUSINESS_CENTER";
      payload.graceDays = 0;
      payload.commercialTerms = {};
    }
    if (config.serviceType) {
      payload.currency = "AED";
      payload.specification = {};
    }
    let endpoint = config.createEndpoint;
    if (config.mode === "unit") {
      endpoint = endpoint.replace("{propertyId}", String(payload.propertyId));
      delete payload.propertyId;
    }
    if (config.title.includes("Holiday")) {
      payload.children = payload.children || 0;
      payload.cleaningAmount = payload.cleaningAmount || 0;
      payload.tourismFee = payload.tourismFee || 0;
      payload.vatAmount = payload.vatAmount || 0;
      payload.currency = "AED";
    }
    setBusy(true);
    setError("");
    try {
      const created = await api<{ data: Row }>(endpoint, csrfToken, {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const imageGroups = config === configs["real-estate"] || config.mode === "propertyAsset"
        ? [["mainPicture", "MAIN"], ["interiorPictures", "INTERIOR"], ["exteriorPictures", "EXTERIOR"], ["floorPlanPictures", "FLOOR_PLAN"], ["pictures", "GENERAL"]] as const
        : [["pictures", "GENERAL"]] as const;
      for (const [fieldName, category] of imageGroups) for (const image of supportsImage ? form.getAll(fieldName).filter((item): item is File => item instanceof File && item.size > 0) : []) {
        const objectType = config === configs.cars ? "VEHICLE" : "PROPERTY";
        const upload = await fetch("/api/v1/admin/media", {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": image.type,
            "x-csrf-token": csrfToken,
            "x-asset-type": objectType,
            "x-object-type": objectType,
            "x-object-id": String(created.data.id),
            "x-asset-title": String(
              payload.titleEn || `${payload.make || ""} ${payload.model || ""}`,
            ).trim(),
            "x-alt-text": String(
              payload.titleEn || `${payload.make || ""} ${payload.model || ""}`,
            ).trim(),
            "x-file-name": image.name,
            "x-media-category": category,
          },
          body: image,
        });
        if (!upload.ok)
          throw new Error(
            "The record was created but its picture could not be uploaded. Open the record and try again.",
          );
      }
      onCreated();
    } catch (value) {
      setError(
        value instanceof Error ? value.message : "Unable to create record",
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <form className="modal operations-modal" onSubmit={submit}>
        <header>
          <div>
            <span className="kicker">Complete record</span>
            <h2>{config.createLabel}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {error && <div className="form-error">{error}</div>}
        <div className="form-grid">
          {config.fields.map((field) => (
            <label
              key={field.name}
              className={
                field.type === "textarea"
                  ? "span-2"
                  : field.type === "checkbox"
                    ? "check span-2"
                    : ""
              }
            >
              {field.type === "checkbox" ? (
                <>
                  <input name={field.name} type="checkbox" />
                  {field.label}
                </>
              ) : (
                <>
                  {field.label}
                  {config === configs["real-estate"] && field.name === "community" ? (
                    <select name={field.name} required={field.required}><option value="">Select UAE area…</option>{propertyMaster.areas.map((x) => <option value={String(x.community)} key={String(x.id)}>{String(x.emirate)} · {String(x.area)} · {String(x.community)}</option>)}</select>
                  ) : config === configs["real-estate"] && field.name === "developerName" ? (
                    <select name={field.name}><option value="">Select developer…</option>{propertyMaster.developers.map((x) => <option value={String(x.name)} key={String(x.id)}>{String(x.name)}</option>)}</select>
                  ) : field.type === "select" ? (
                    <select name={field.name} required={field.required}>
                      <option value="">Select…</option>
                      {optionsFor(
                        field,
                        entities,
                        parties,
                        units,
                        properties,
                        vehicles,
                      ).map(([value, label]) => (
                        <option value={value} key={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "textarea" ? (
                    <textarea
                      name={field.name}
                      rows={3}
                      required={field.required}
                    />
                  ) : (
                    <input
                      name={field.name}
                      type={field.type || "text"}
                      required={field.required}
                      min={field.type === "number" ? "0" : undefined}
                      step={field.type === "number" ? "any" : undefined}
                    />
                  )}
                </>
              )}
            </label>
          ))}
          {supportsImage && config !== configs["real-estate"] && (
            <label className="span-2 media-picker">
              <ImagePlus />
              Pictures and floor plans
              <input
                name="pictures"
                type="file"
                multiple
                accept="image/jpeg,image/png,image/webp,image/avif"
              />
              <small>
                Select multiple JPG, PNG, WebP or AVIF files. The server scans
                and compresses every image automatically.
              </small>
            </label>
          )}
          {config === configs["real-estate"] && [["mainPicture", "Main listing picture"], ["interiorPictures", "Interior photos"], ["exteriorPictures", "Exterior photos"], ["floorPlanPictures", "Floor plans"]].map(([name, text], index) => (
            <label className="span-2 media-picker" key={name}><ImagePlus/>{text}<input name={name} type="file" multiple={index !== 0} required={index === 0} accept="image/jpeg,image/png,image/webp,image/avif"/><small>{index === 0 ? "Required. This image appears in property lists, cards, the website and portal feeds." : "Select multiple files. Each image is scanned, compressed and kept in its correct property category."}</small></label>
          ))}
        </div>
        <footer>
          <button type="button" className="button secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="button primary" disabled={busy}>
            {busy ? "Saving…" : "Save complete record"}
          </button>
        </footer>
      </form>
    </div>
  );
}
function optionsFor(
  field: Field,
  entities: Entity[],
  parties: Party[],
  units: Unit[],
  properties: Property[],
  vehicles: Vehicle[],
): Array<[string, string]> {
  if (field.source === "entities")
    return entities.map((x) => [x.id, x.trade_name || x.legal_name_en]);
  if (field.source === "parties")
    return parties.map((x) => [x.id, x.display_name]);
  if (field.source === "units")
    return units.map((x) => [x.id, `${x.property_name} · ${x.unit_number}`]);
  if (field.source === "properties")
    return properties.map((x) => [x.id, `${x.reference} · ${x.title_en}`]);
  if (field.source === "vehicles")
    return vehicles.map((x) => [
      x.id,
      `${x.plate_number} · ${x.make} ${x.model}`,
    ]);
  return field.options || [];
}

const tenancyStatuses = [
  "DEPOSIT_PENDING",
  "DEPOSIT_VERIFICATION_PENDING",
  "RESERVED",
  "DOCUMENTS_PENDING",
  "TENANCY_PREPARATION",
  "MANAGEMENT_APPROVAL_PENDING",
  "TENANT_SIGNATURE_PENDING",
  "FINANCE_CLEARANCE_PENDING",
  "EJARI_PENDING",
  "IT_ASSESSMENT",
  "OFFICE_PREPARATION",
  "READY_FOR_HANDOVER",
  "ACTIVE_TENANT",
  "RENEWAL_PENDING",
  "VACATING",
  "VACATED",
];
function StatusDrawer({
  row,
  config,
  onClose,
  csrfToken,
  onChanged,
  canEditProperty,
  canManagePropertyMedia,
  canCommentProperty,
  canPublishProperty,
}: {
  row: Row | null;
  config: Config;
  onClose: () => void;
  csrfToken: string | null;
  onChanged: () => void;
  canEditProperty: boolean;
  canManagePropertyMedia: boolean;
  canCommentProperty: boolean;
  canPublishProperty: boolean;
}) {
  const [busy, setBusy] = useState(false);
  if (!row) return null;
  if (config === extraConfigs.fleetBookings)
    return (
      <RentalBookingDrawer
        row={row}
        onClose={onClose}
        csrfToken={csrfToken}
        onChanged={onChanged}
      />
    );
  if (
    config === configs["real-estate"] ||
    config === configs.cars ||
    config.mode === "propertyAsset"
  )
    return (
      <AssetDrawer
        row={row}
        kind={config === configs.cars ? "VEHICLE" : "PROPERTY"}
        onClose={onClose}
        csrfToken={csrfToken}
        onChanged={onChanged}
        canEditProperty={canEditProperty}
        canManagePropertyMedia={canManagePropertyMedia}
        canCommentProperty={canCommentProperty}
        canPublishProperty={canPublishProperty}
      />
    );
  const statuses =
    config.mode === "tenancy"
      ? tenancyStatuses
      : config === extraConfigs.listings
        ? [
            "DRAFT",
            "APPROVAL_PENDING",
            "ACTIVE",
            "PAUSED",
            "UNDER_OFFER",
            "LET",
            "SOLD",
            "EXPIRED",
            "WITHDRAWN",
          ]
        : [
            "HELD",
            "DEPOSITED",
            "CLEARED",
            "BOUNCED",
            "RETURNED",
            "CANCELLED",
            "REPLACED",
          ];
  const base =
    config.mode === "tenancy"
      ? "/api/v1/business-center/tenancies"
      : config === extraConfigs.listings
        ? "/api/v1/real-estate/listings"
        : "/api/v1/finance/cheques";
  async function change(status: string) {
    if (!csrfToken) return;
    setBusy(true);
    try {
      await api(`${base}/${row!.id}/status`, csrfToken, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <aside className="record-drawer">
        <header>
          <div>
            <span className="kicker">Controlled workflow</span>
            <h2>{String(row.reference || row.cheque_number || "Record")}</h2>
            <p>
              {String(
                row.tenant_name ||
                  row.property_name ||
                  row.party_name ||
                  config.tabLabel ||
                  config.title,
              )}
            </p>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        <section>
          <h3>Current status</h3>
          <span className="status">
            {String(row.status || "DRAFT").replaceAll("_", " ")}
          </span>
        </section>
        <section>
          <h3>Move to stage</h3>
          <div className="stage-grid">
            {statuses.map((status) => (
              <button
                disabled={busy}
                className={status === row.status ? "active" : ""}
                onClick={() => void change(status)}
                key={status}
              >
                {status.replaceAll("_", " ")}
              </button>
            ))}
          </div>
        </section>
        <section>
          <h3>Record controls</h3>
          <p className="drawer-note">
            Every change is written to the audit trail. Finance clearance,
            contract execution and handover timestamps are applied by the
            controlled workflow.
          </p>
        </section>
      </aside>
    </div>
  );
}

function RentalBookingDrawer({
  row,
  onClose,
  csrfToken,
  onChanged,
}: {
  row: Row;
  onClose: () => void;
  csrfToken: string | null;
  onChanged: () => void;
}) {
  const [detail, setDetail] = useState<Row | null>(null),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState("");
  async function load() {
    try {
      setDetail(
        (await api<{ data: Row }>(`/api/v1/fleet/bookings/${row.id}`)).data,
      );
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Rental could not be loaded",
      );
    }
  }
  useEffect(() => {
    void load();
  }, [row.id]);
  async function status(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api(`/api/v1/fleet/bookings/${row.id}`, csrfToken, {
        method: "PATCH",
        body: JSON.stringify({
          status: d.status,
          depositStatus: d.depositStatus,
          driverName: d.driverName || null,
          driverMobile: d.driverMobile || null,
          licenceNumber: d.licenceNumber || null,
          licenceCountry: d.licenceCountry || null,
          licenceExpiry: d.licenceExpiry || null,
        }),
      });
      setMessage("Rental status and vehicle availability updated.");
      await load();
      onChanged();
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Rental update failed",
      );
    } finally {
      setBusy(false);
    }
  }
  async function inspection(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api(`/api/v1/fleet/bookings/${row.id}/inspections`, csrfToken, {
        method: "POST",
        body: JSON.stringify({
          inspectionType: d.inspectionType,
          odometerKm: Number(d.odometerKm),
          fuelLevelPct: Number(d.fuelLevelPct),
          cleanliness: d.cleanliness,
          bodyCondition: d.bodyCondition,
          tyreCondition: d.tyreCondition,
          keysCount: Number(d.keysCount),
          damageNotes: d.damageNotes || null,
          checklist: {
            spareTyre: d.spareTyre === "on",
            warningTriangle: d.warningTriangle === "on",
            registrationCard: d.registrationCard === "on",
          },
        }),
      });
      setMessage("Signed handover checklist recorded.");
      await load();
    } catch (value) {
      setMessage(
        value instanceof Error
          ? value.message
          : "Inspection could not be saved",
      );
    } finally {
      setBusy(false);
    }
  }
  async function uploadDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const form = new FormData(event.currentTarget),
      file = form.get("document");
    if (!(file instanceof File)) return;
    setBusy(true);
    try {
      const response = await fetch(
        `/api/v1/fleet/bookings/${row.id}/documents`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": file.type,
            "x-csrf-token": csrfToken,
            "x-document-type": String(form.get("documentType")),
            "x-holder-name": String(form.get("holderName") || ""),
            "x-document-number": String(form.get("documentNumber") || ""),
            "x-issuing-country": String(form.get("issuingCountry") || ""),
            "x-expiry-date": String(form.get("expiryDate") || ""),
            "x-file-name": file.name,
          },
          body: file,
        },
      );
      if (!response.ok) throw new Error("Driver document upload failed");
      setMessage("Driver document scanned, compressed and retained.");
      event.currentTarget.reset();
      await load();
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Document upload failed",
      );
    } finally {
      setBusy(false);
    }
  }
  const inspections = (detail?.inspections || []) as Row[],
    documents = (detail?.documents || []) as Row[],
    charges = (detail?.charges || []) as Row[];
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <aside className="record-drawer rental-drawer">
        <header>
          <div>
            <span className="kicker">Royal Rentacar · rental agreement</span>
            <h2>
              {String(
                detail?.agreement_number || detail?.reference || row.reference,
              )}
            </h2>
            <p>
              {String(detail?.customer_name || row.customer_name)} ·{" "}
              {String(detail?.make || row.make)}{" "}
              {String(detail?.model || row.model)}
            </p>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {message && <div className="notice-banner">{message}</div>}
        {detail ? (
          <>
            <section className="record-facts">
              <article>
                <span>Pickup</span>
                <strong>
                  {new Date(String(detail.pickup_at)).toLocaleString("en-AE")}
                </strong>
              </article>
              <article>
                <span>Return</span>
                <strong>
                  {new Date(String(detail.return_at)).toLocaleString("en-AE")}
                </strong>
              </article>
              <article>
                <span>Agreement value</span>
                <strong>
                  AED {Number(detail.base_amount || 0).toLocaleString()}
                </strong>
              </article>
              <article>
                <span>Contracting entity</span>
                <strong>Royal Rentacar</strong>
              </article>
            </section>
            <form className="asset-form" onSubmit={status}>
              <label>
                Status
                <select name="status" defaultValue={String(detail.status)}>
                  {[
                    "QUOTE",
                    "CONFIRMED",
                    "RESERVED",
                    "CHECKED_OUT",
                    "IN_PROGRESS",
                    "RETURN_DUE",
                    "CHECKED_IN",
                    "COMPLETED",
                    "CANCELLED",
                    "CLOSED",
                  ].map((x) => (
                    <option key={x}>{x}</option>
                  ))}
                </select>
              </label>
              <label>
                Deposit
                <select
                  name="depositStatus"
                  defaultValue={String(detail.deposit_status || "PENDING")}
                >
                  <option>PENDING</option>
                  <option>HELD</option>
                  <option>PAID</option>
                  <option>PARTIAL_REFUND</option>
                  <option>REFUNDED</option>
                  <option>FORFEITED</option>
                </select>
              </label>
              <label>
                Driver name
                <input
                  name="driverName"
                  defaultValue={String(detail.driver_name || "")}
                />
              </label>
              <label>
                Driver mobile
                <input
                  name="driverMobile"
                  defaultValue={String(detail.driver_mobile || "")}
                />
              </label>
              <label>
                Licence number
                <input
                  name="licenceNumber"
                  defaultValue={String(detail.licence_number || "")}
                />
              </label>
              <label>
                Licence country
                <input
                  name="licenceCountry"
                  maxLength={2}
                  defaultValue={String(detail.licence_country || "AE")}
                />
              </label>
              <label>
                Licence expiry
                <input
                  name="licenceExpiry"
                  type="date"
                  defaultValue={String(detail.licence_expiry || "").slice(
                    0,
                    10,
                  )}
                />
              </label>
              <button className="button primary" disabled={busy}>
                Save rental
              </button>
            </form>
            <details open>
              <summary>Checkout / return inspection</summary>
              <form className="asset-form lifecycle-form" onSubmit={inspection}>
                <label>
                  Inspection
                  <select name="inspectionType">
                    <option>CHECK_OUT</option>
                    <option>CHECK_IN</option>
                  </select>
                </label>
                <label>
                  Odometer km
                  <input name="odometerKm" type="number" required />
                </label>
                <label>
                  Fuel level %
                  <input
                    name="fuelLevelPct"
                    type="number"
                    min="0"
                    max="100"
                    required
                  />
                </label>
                <label>
                  Cleanliness
                  <select name="cleanliness">
                    <option>EXCELLENT</option>
                    <option>GOOD</option>
                    <option>ACCEPTABLE</option>
                    <option>REQUIRES_CLEANING</option>
                  </select>
                </label>
                <label>
                  Body condition
                  <select name="bodyCondition">
                    <option>NO_VISIBLE_DAMAGE</option>
                    <option>EXISTING_DAMAGE</option>
                    <option>NEW_DAMAGE</option>
                  </select>
                </label>
                <label>
                  Tyres
                  <select name="tyreCondition">
                    <option>GOOD</option>
                    <option>ATTENTION</option>
                    <option>REPLACEMENT_REQUIRED</option>
                  </select>
                </label>
                <label>
                  Keys returned
                  <input
                    name="keysCount"
                    type="number"
                    min="0"
                    defaultValue="2"
                  />
                </label>
                <label className="span-2">
                  Damage notes
                  <textarea name="damageNotes" rows={3} />
                </label>
                <label className="check">
                  <input name="spareTyre" type="checkbox" />
                  Spare tyre
                </label>
                <label className="check">
                  <input name="warningTriangle" type="checkbox" />
                  Warning triangle
                </label>
                <label className="check">
                  <input name="registrationCard" type="checkbox" />
                  Registration card
                </label>
                <button className="button primary" disabled={busy}>
                  Record inspection
                </button>
              </form>
            </details>
            <details>
              <summary>
                Driver identity & licence documents ({documents.length})
              </summary>
              <form
                className="asset-form lifecycle-form"
                onSubmit={uploadDocument}
              >
                <label>
                  Type
                  <select name="documentType">
                    <option>DRIVING_LICENCE</option>
                    <option>EMIRATES_ID</option>
                    <option>PASSPORT</option>
                    <option>VISA</option>
                    <option>INTERNATIONAL_PERMIT</option>
                    <option>OTHER</option>
                  </select>
                </label>
                <label>
                  Holder name
                  <input name="holderName" />
                </label>
                <label>
                  Number
                  <input name="documentNumber" />
                </label>
                <label>
                  Issuing country
                  <input
                    name="issuingCountry"
                    maxLength={2}
                    defaultValue="AE"
                  />
                </label>
                <label>
                  Expiry
                  <input name="expiryDate" type="date" />
                </label>
                <label>
                  File
                  <input
                    name="document"
                    type="file"
                    required
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                  />
                </label>
                <button className="button primary" disabled={busy}>
                  Upload secure document
                </button>
              </form>
            </details>
            <section>
              <h3>Full rental cycle</h3>
              <div className="rental-cycle">
                {[
                  "Quote",
                  "Confirmed",
                  "Reserved",
                  "Checked out",
                  "Return due",
                  "Checked in",
                  "Closed",
                ].map((stage) => (
                  <span
                    className={
                      String(detail.status || "")
                        .replaceAll("_", " ")
                        .toLowerCase()
                        .replace(/\b\w/g, (x) => x.toUpperCase()) === stage
                        ? "active"
                        : ""
                    }
                    key={stage}
                  >
                    {stage}
                  </span>
                ))}
              </div>
              <p className="drawer-note">
                {inspections.length} inspections · {documents.length} driver
                documents · {charges.length} additional charges retained.
              </p>
            </section>
          </>
        ) : (
          <div className="empty">Loading rental agreement…</div>
        )}
      </aside>
    </div>
  );
}

function AssetDrawer({
  row,
  kind,
  onClose,
  csrfToken,
  onChanged,
  canEditProperty,
  canManagePropertyMedia,
  canCommentProperty,
  canPublishProperty,
}: {
  row: Row;
  kind: "PROPERTY" | "VEHICLE";
  onClose: () => void;
  csrfToken: string | null;
  onChanged: () => void;
  canEditProperty: boolean;
  canManagePropertyMedia: boolean;
  canCommentProperty: boolean;
  canPublishProperty: boolean;
}) {
  const [detail, setDetail] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [propertyTab, setPropertyTab] = useState("OVERVIEW");
  const [comments, setComments] = useState<Row[]>([]);
  const [integrations, setIntegrations] = useState<Row[]>([]);
  const base =
    kind === "PROPERTY"
      ? "/api/v1/real-estate/properties"
      : "/api/v1/fleet/vehicles";
  async function load() {
    try {
      const result = await api<{ data: Row }>(`${base}/${row.id}`);
      setDetail(result.data);
      if (kind === "PROPERTY") {
        const [commentResult, integrationResult] = await Promise.all([
          api<{ data: Row[] }>(`/api/v1/enterprise/record/PROPERTY/${row.id}/comments`),
          canPublishProperty ? api<{data:Row[]}>("/api/v1/admin/integrations").catch(() => ({data:[]})) : Promise.resolve({data:[] as Row[]}),
        ]);
        setComments(commentResult.data);
        setIntegrations(integrationResult.data.filter((x) => ["BAYUT","PROPERTY_FINDER","DUBIZZLE"].includes(String(x.connector))));
      }
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Unable to load details",
      );
    }
  }
  useEffect(() => {
    void load();
  }, [row.id]);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const form = new FormData(event.currentTarget);
    const value = (name: string) => String(form.get(name) || "").trim();
    const number = (name: string) => (value(name) ? Number(value(name)) : null);
    const payload =
      kind === "PROPERTY"
        ? {
            titleEn: value("titleEn"),
            propertyType: value("propertyType"),
            emirate: value("emirate"),
            city: value("city") || null,
            community: value("community") || null,
            building: value("building") || null,
            addressEn: value("addressEn") || null,
            descriptionEn: value("descriptionEn") || null,
            ownershipType: value("ownershipType"),
            availabilityStatus: value("availabilityStatus"),
            bedrooms: number("bedrooms"),
            bathrooms: number("bathrooms"),
            areaSqft: number("areaSqft"),
            plotSqft: number("plotSqft"),
            askingPrice: number("askingPrice"),
            currency: value("currency") || "AED",
            furnishedStatus: value("furnishedStatus") || null,
            parkingCount: number("parkingCount"),
            completionStatus: value("completionStatus") || null,
            developerName: value("developerName") || null,
            viewType: value("viewType") || null,
            listedPublicly: form.get("listedPublicly") === "on",
            features: value("features")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
            category: value("category") || "RESIDENTIAL",
            usageType: value("usageType") || "END_USE",
            listingType: value("listingType") || "SALE",
            listingSource: value("listingSource") || null,
            unitNumber: value("unitNumber") || null,
            floorNumber: value("floorNumber") || null,
            virtualTourUrl: value("virtualTourUrl") || null,
            locationDetails: {
              postalCode: value("postalCode") || null,
              latitude: number("latitude"),
              longitude: number("longitude"),
              mapUrl: value("mapUrl") || null,
            },
            financialDetails: {
              marketValue: number("marketValue"),
              salePricePerSqft: number("salePricePerSqft"),
              monthlyRent: number("monthlyRent"),
              yearlyRent: number("yearlyRent"),
              rentalYieldPct: number("rentalYieldPct"),
              capitalAppreciationPct: number("capitalAppreciationPct"),
              serviceCharge: number("serviceCharge"),
              offeredCommissionPct: number("offeredCommissionPct"),
              taxImplications: value("taxImplications") || null,
            },
            purchaseDetails: {
              purchasePrice: number("purchasePrice"),
              purchaseDate: value("purchaseDate") || null,
              ownershipFinanceType: value("ownershipFinanceType") || null,
              purchaseRemarks: value("purchaseRemarks") || null,
            },
            roomDetails: value("roomDetailsJson")
              ? JSON.parse(value("roomDetailsJson"))
              : detail?.room_details || [],
            additionalDetails: {
              balconies: number("balconies"),
              maidRoom: value("maidRoom") === "YES",
              study: value("study") === "YES",
              vacantOnTransfer: value("vacantOnTransfer") === "YES",
              handoverDate: value("handoverDate") || null,
              titleDeedNumber: value("titleDeedNumber") || null,
              permitNumber: value("permitNumber") || null,
            },
            sourceDetails: {
              broker: value("broker") || null,
              brokerAgent: value("brokerAgent") || null,
              development: value("development") || null,
            },
          }
        : {
            status: value("status"),
            description: value("description") || null,
            odometerKm: number("odometerKm"),
            transmission: value("transmission") || null,
            fuelType: value("fuelType") || null,
            seats: number("seats"),
            doors: number("doors"),
            dailyRate: number("dailyRate"),
            weeklyRate: number("weeklyRate"),
            monthlyRate: number("monthlyRate"),
            depositAmount: number("depositAmount"),
            registrationExpiry: value("registrationExpiry") || null,
            insuranceExpiry: value("insuranceExpiry") || null,
            bodyType: value("bodyType") || null,
            engineNumber: value("engineNumber") || null,
            engineSize: value("engineSize") || null,
            trimLevel: value("trimLevel") || null,
            driveType: value("driveType") || null,
            keysCount: number("keysCount"),
            ownershipStatus: value("ownershipStatus") || null,
            acquisitionDate: value("acquisitionDate") || null,
            warrantyExpiry: value("warrantyExpiry") || null,
            listedPublicly: form.get("listedPublicly") === "on",
            features: value("features")
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
          };
    setBusy(true);
    setMessage("");
    try {
      await api(`${base}/${row.id}`, csrfToken, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      setMessage("Saved with audit history.");
      await load();
      onChanged();
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }
  async function upload(files: FileList | File[], category = "GENERAL") {
    if (!csrfToken || !files.length) return;
    setBusy(true);
    setMessage("");
    try {
      const title =
        kind === "PROPERTY"
          ? String(detail?.title_en || row.reference)
          : `${String(detail?.make || "")} ${String(detail?.model || "")}`;
      for (const file of Array.from(files)) {
        const response = await fetch("/api/v1/admin/media", {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": file.type,
            "x-csrf-token": csrfToken,
            "x-asset-type": kind,
            "x-object-type": kind,
            "x-object-id": String(row.id),
            "x-asset-title": title,
            "x-alt-text": title,
            "x-file-name": file.name,
            "x-media-category": category,
          },
          body: file,
        });
        if (!response.ok)
          throw new Error(`Picture upload failed: ${file.name}`);
      }
      setMessage(
        `${files.length} picture${files.length === 1 ? "" : "s"} scanned, compressed and attached.`,
      );
      await load();
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "Upload failed");
    } finally {
      setBusy(false);
    }
  }
  async function addPropertyComment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const body = String(new FormData(event.currentTarget).get("body") || "").trim();
    if (!body) return;
    setBusy(true);
    try {
      await api(`/api/v1/enterprise/record/PROPERTY/${row.id}/comments`, csrfToken, { method: "POST", body: JSON.stringify({ body, visibility: "INTERNAL" }) });
      event.currentTarget.reset();
      await load();
      setMessage("Internal property comment added.");
    } catch (value) { setMessage(value instanceof Error ? value.message : "Comment could not be added"); }
    finally { setBusy(false); }
  }
  async function publishProperty(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    setMessage("");
    try {
      const result = await api<{message?:string}>(`/api/v1/real-estate/properties/${row.id}/portal-publications`, csrfToken, {
        method: "POST",
        body: JSON.stringify({ integrationId: d.integrationId, action: d.action }),
      });
      setMessage(result.message || "Portal publication updated.");
      await load();
    } catch (value) {
      setMessage(value instanceof Error ? value.message : "Portal publication failed");
    } finally {
      setBusy(false);
    }
  }
  async function addVehicleEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api(`/api/v1/fleet/vehicles/${row.id}/events`, csrfToken, {
        method: "POST",
        body: JSON.stringify({
          eventType: d.eventType,
          eventAt: d.eventAt
            ? new Date(String(d.eventAt)).toISOString()
            : undefined,
          odometerKm: d.odometerKm ? Number(d.odometerKm) : null,
          fuelLevelPct: d.fuelLevelPct ? Number(d.fuelLevelPct) : null,
          vendor: d.vendor || null,
          cost: d.cost ? Number(d.cost) : null,
          notes: d.notes || null,
          checklist: { roadworthy: d.roadworthy === "on" },
        }),
      });
      setMessage("Fleet event recorded in the permanent vehicle history.");
      event.currentTarget.reset();
      await load();
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Event could not be recorded",
      );
    } finally {
      setBusy(false);
    }
  }
  async function uploadVehicleDocument(
    file: File,
    type: string,
    number: string,
    expiry: string,
  ) {
    if (!csrfToken || !file.size) return;
    setBusy(true);
    try {
      const response = await fetch(
        `/api/v1/fleet/vehicles/${row.id}/documents`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": file.type,
            "x-csrf-token": csrfToken,
            "x-document-type": type,
            "x-document-number": number,
            "x-expiry-date": expiry,
            "x-file-name": file.name,
          },
          body: file,
        },
      );
      if (!response.ok) throw new Error("Vehicle document upload failed");
      setMessage("Document malware-scanned, compressed and attached.");
      await load();
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Document upload failed",
      );
    } finally {
      setBusy(false);
    }
  }
  async function linkPropertyDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const d = Object.fromEntries(new FormData(event.currentTarget));
    setBusy(true);
    try {
      await api(
        `/api/v1/real-estate/properties/${row.id}/documents/link`,
        csrfToken,
        {
          method: "POST",
          body: JSON.stringify({
            documentType: d.documentType,
            title: d.title,
            mediaAssetId: d.mediaAssetId || null,
            externalReference: d.externalReference || null,
            remarks: d.remarks || null,
          }),
        },
      );
      setMessage("Property/deal document linked with audit history.");
      event.currentTarget.reset();
      await load();
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Document could not be linked",
      );
    } finally {
      setBusy(false);
    }
  }
  async function uploadPropertyDocument(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!csrfToken) return;
    const form = new FormData(event.currentTarget),
      file = form.get("document");
    if (!(file instanceof File) || !file.size) return;
    setBusy(true);
    setMessage("");
    try {
      const response = await fetch(
        `/api/v1/real-estate/properties/${row.id}/documents`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "content-type": file.type,
            "x-csrf-token": csrfToken,
            "x-document-type": String(form.get("documentType") || "OTHER"),
            "x-document-title": String(form.get("title") || file.name),
            "x-file-name": file.name,
            "x-external-reference": String(form.get("externalReference") || ""),
            "x-remarks": String(form.get("remarks") || ""),
          },
          body: file,
        },
      );
      if (!response.ok)
        throw new Error(`Document upload failed (${response.status})`);
      setMessage(
        "Document malware-scanned, compressed and attached to the property dossier.",
      );
      event.currentTarget.reset();
      await load();
    } catch (value) {
      setMessage(
        value instanceof Error ? value.message : "Document upload failed",
      );
    } finally {
      setBusy(false);
    }
  }
  const media = (detail?.media || []) as Row[];
  const events = (detail?.events || []) as Row[];
  const documents = (detail?.documents || []) as Row[];
  const bookings = (detail?.bookings || []) as Row[];
  const features = Array.isArray(detail?.features)
    ? (detail?.features as string[]).join(", ")
    : "";
  return (
    <div
      className="drawer-backdrop"
      onMouseDown={(e) => {
        if (e.currentTarget === e.target) onClose();
      }}
    >
      <aside
        className={`record-drawer asset-drawer ${kind === "PROPERTY" ? "property-record" : ""}`}
      >
        <header>
          <div>
            <span className="kicker">
              {kind === "PROPERTY" ? "Property profile" : "Fleet asset profile"}
            </span>
            <h2>
              {String(
                detail?.title_en ||
                  `${detail?.make || ""} ${detail?.model || ""}` ||
                  row.reference,
              )}
            </h2>
            <p>{String(detail?.reference || row.reference)}</p>
          </div>
          <button className="icon-button" onClick={onClose}>
            <X />
          </button>
        </header>
        {message && <div className="notice-banner">{message}</div>}
        <section className="media-gallery">
          {media.map((item) => (
            <figure key={String(item.id)}>
              <img src={`/api/v1/media/${item.id}/content`} alt={String(item.alt_text || item.title || "Asset picture")} />
              {kind === "PROPERTY" && <figcaption>{String(item.media_category || "GENERAL").replaceAll("_", " ")}</figcaption>}
            </figure>
          ))}
          {canManagePropertyMedia && <label className="media-add">
            <ImagePlus />
            <span>{kind === "PROPERTY" ? "Add general pictures" : "Add pictures"}</span>
            <input
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp,image/avif"
              onChange={(event) => {
                const files = event.target.files;
                if (files?.length) void upload(files, "GENERAL");
              }}
            />
          </label>}
        </section>
        {kind === "PROPERTY" && canManagePropertyMedia && (
          <section className="property-media-zones">
            {[["MAIN", "Main listing picture"], ["INTERIOR", "Interior photos"], ["EXTERIOR", "Exterior photos"], ["FLOOR_PLAN", "Floor plans"]].map(([category, title]) => (
              <label className="media-zone" key={category}><ImagePlus/><strong>{title}</strong><small>Multiple images allowed</small><input type="file" multiple accept="image/jpeg,image/png,image/webp,image/avif" onChange={(event) => { const files = event.target.files; if (files?.length) void upload(files, category); }}/></label>
            ))}
          </section>
        )}
        {kind === "PROPERTY" && detail && (
          <>
            <section className="property-identity">
              <div>
                <span className="kicker">
                  {String(detail.category || "Residential")} ·{" "}
                  {String(detail.listing_type || "Sale")}
                </span>
                <h2>{String(detail.title_en)}</h2>
                <p>
                  {String(
                    detail.building || detail.community || detail.city || "",
                  )}{" "}
                  {detail.unit_number ? `· Unit ${detail.unit_number}` : ""}
                </p>
              </div>
              <div className="property-badges">
                <span>
                  {String(detail.ownership_type || "CLIENT").replaceAll(
                    "_",
                    " ",
                  )}
                </span>
                <strong>
                  {String(detail.availability_status || "AVAILABLE").replaceAll(
                    "_",
                    " ",
                  )}
                </strong>
              </div>
            </section>
            <nav
              className="property-tabs"
              aria-label="Property record sections"
            >
              {[
                "OVERVIEW",
                "LOCATION",
                "FINANCIAL",
                "PURCHASE",
                "ROOMS",
                "DOCUMENTS",
                ...(canPublishProperty ? ["PORTALS"] : []),
                "COMMENTS",
                ...(canEditProperty ? ["EDIT"] : []),
              ].map((tab) => (
                <button
                  type="button"
                  className={propertyTab === tab ? "active" : ""}
                  onClick={() => setPropertyTab(tab)}
                  key={tab}
                >
                  {tab.replace("DOCUMENTS", "Images & documents").replace("COMMENTS", "Internal comments")}
                </button>
              ))}
            </nav>
            {propertyTab === "OVERVIEW" && (
              <section className="property-tab-panel">
                <h3>Property details</h3>
                <div className="property-facts">
                  {[
                    ["Category", detail.category],
                    ["Usage", detail.usage_type],
                    ["Listing type", detail.listing_type],
                    ["Property type", detail.property_type],
                    [
                      "Total area",
                      detail.area_sqft ? `${detail.area_sqft} sqft` : "—",
                    ],
                    [
                      "Bedrooms / bathrooms",
                      `${detail.bedrooms || 0} / ${detail.bathrooms || 0}`,
                    ],
                    ["Furnishing", detail.furnished_status],
                    ["Developer", detail.developer_name],
                    ["Completion", detail.completion_status],
                  ].map(([a, b]) => (
                    <article key={String(a)}>
                      <span>{String(a)}</span>
                      <strong>
                        {String(b || "Not recorded").replaceAll("_", " ")}
                      </strong>
                    </article>
                  ))}
                </div>
                <h3>Description</h3>
                <p className="property-description">
                  {String(detail.description_en || "No description recorded.")}
                </p>
              </section>
            )}
            {propertyTab === "LOCATION" && (
              <section className="property-tab-panel">
                <h3>Location details</h3>
                <div className="property-facts">
                  {[
                    ["Address", detail.address_en],
                    ["Community", detail.community],
                    ["Building / project", detail.building],
                    ["City", detail.city],
                    ["Emirate", detail.emirate],
                    [
                      "Postal code",
                      (detail.location_details as Row)?.postalCode,
                    ],
                    ["Map", (detail.location_details as Row)?.mapUrl],
                  ].map(([a, b]) => (
                    <article key={String(a)}>
                      <span>{String(a)}</span>
                      <strong>{String(b || "Not recorded")}</strong>
                    </article>
                  ))}
                </div>
              </section>
            )}
            {propertyTab === "FINANCIAL" && (
              <section className="property-tab-panel">
                <h3>Financial intelligence</h3>
                <div className="property-facts financial">
                  {[
                    [
                      "Asking price",
                      `${detail.currency || "AED"} ${detail.asking_price || 0}`,
                    ],
                    [
                      "Market value",
                      `${detail.currency || "AED"} ${(detail.financial_details as Row)?.marketValue || 0}`,
                    ],
                    [
                      "Monthly rent",
                      `${detail.currency || "AED"} ${(detail.financial_details as Row)?.monthlyRent || 0}`,
                    ],
                    [
                      "Yearly rent",
                      `${detail.currency || "AED"} ${(detail.financial_details as Row)?.yearlyRent || 0}`,
                    ],
                    [
                      "Rental yield",
                      `${(detail.financial_details as Row)?.rentalYieldPct || 0}%`,
                    ],
                    [
                      "Capital appreciation",
                      `${(detail.financial_details as Row)?.capitalAppreciationPct || 0}%`,
                    ],
                    [
                      "Service charge",
                      `${detail.currency || "AED"} ${(detail.financial_details as Row)?.serviceCharge || 0}`,
                    ],
                    [
                      "Offered commission",
                      `${(detail.financial_details as Row)?.offeredCommissionPct || 0}%`,
                    ],
                  ].map(([a, b]) => (
                    <article key={String(a)}>
                      <span>{String(a)}</span>
                      <strong>{String(b)}</strong>
                    </article>
                  ))}
                </div>
              </section>
            )}
            {propertyTab === "PURCHASE" && (
              <section className="property-tab-panel">
                <h3>Purchase information</h3>
                <div className="property-facts">
                  {[
                    [
                      "Purchase price",
                      `${detail.currency || "AED"} ${(detail.purchase_details as Row)?.purchasePrice || 0}`,
                    ],
                    [
                      "Date of purchase",
                      (detail.purchase_details as Row)?.purchaseDate,
                    ],
                    [
                      "Ownership / finance",
                      (detail.purchase_details as Row)?.ownershipFinanceType,
                    ],
                    [
                      "Remarks",
                      (detail.purchase_details as Row)?.purchaseRemarks,
                    ],
                  ].map(([a, b]) => (
                    <article key={String(a)}>
                      <span>{String(a)}</span>
                      <strong>{String(b || "Not recorded")}</strong>
                    </article>
                  ))}
                </div>
              </section>
            )}
            {propertyTab === "ROOMS" && (
              <section className="property-tab-panel">
                <h3>Room details</h3>
                <div className="table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>Room</th>
                        <th>Type</th>
                        <th>Size</th>
                        <th>Ensuite</th>
                      </tr>
                    </thead>
                    <tbody>
                      {((detail.room_details || []) as Row[]).map((x, i) => (
                        <tr key={i}>
                          <td>{String(x.name || `Room ${i + 1}`)}</td>
                          <td>{String(x.type || "—")}</td>
                          <td>{String(x.sizeSqft || "—")} sqft</td>
                          <td>{x.ensuite ? "Yes" : "No"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {!(detail.room_details as Row[])?.length && (
                  <div className="empty">Add room details from Edit.</div>
                )}
              </section>
            )}
            {propertyTab === "DOCUMENTS" && (
              <section className="property-tab-panel">
                <h3>Images & deal documents</h3>
                <div className="document-cards">
                  {documents.map((x) => (
                    <article key={String(x.id)}>
                      <strong>{String(x.title)}</strong>
                      <span>
                        {String(x.document_type).replaceAll("_", " ")}
                      </span>
                      <small>
                        {String(
                          x.external_reference ||
                            x.remarks ||
                            "Linked to this property",
                        )}
                      </small>
                      {Boolean(x.original_name) && (
                        <a
                          href={`/api/v1/real-estate/property-documents/${x.id}/content`}
                        >
                          Download {String(x.original_name)}
                        </a>
                      )}
                    </article>
                  ))}
                </div>
                <form
                  className="asset-form compact"
                  onSubmit={uploadPropertyDocument}
                >
                  <label>
                    Upload type
                    <select name="documentType">
                      <option>TITLE_DEED</option>
                      <option>OQOOD</option>
                      <option>FORM_A</option>
                      <option>FORM_B</option>
                      <option>FORM_F</option>
                      <option>FORM_I</option>
                      <option>SPA</option>
                      <option>TENANCY_CONTRACT</option>
                      <option>NOC</option>
                      <option>FLOOR_PLAN</option>
                      <option>OWNER_PASSPORT</option>
                      <option>OWNER_EMIRATES_ID</option>
                      <option>OWNER_POA</option>
                      <option>OWNER_COMPANY_DOCUMENT</option>
                      <option>OWNER_BANK_DETAILS</option>
                      <option>COMMISSION_INVOICE</option>
                      <option>COMMISSION_RECEIPT</option>
                      <option>SPLIT_APPROVAL</option>
                      <option>PAYMENT_PROOF</option>
                      <option>OTHER</option>
                    </select>
                  </label>
                  <label>
                    Document title
                    <input name="title" required />
                  </label>
                  <label>
                    PDF or image
                    <input
                      name="document"
                      type="file"
                      required
                      accept="application/pdf,image/jpeg,image/png,image/webp"
                    />
                  </label>
                  <label>
                    External / DMS reference
                    <input name="externalReference" />
                  </label>
                  <label className="span-2">
                    Remarks
                    <input name="remarks" />
                  </label>
                  <button className="button primary span-2" disabled={busy}>
                    Scan & upload document
                  </button>
                </form>
                <h3>Link an existing image or DMS record</h3>
                <form
                  className="asset-form compact"
                  onSubmit={linkPropertyDocument}
                >
                  <label>
                    Document type
                    <select name="documentType">
                      <option>TITLE_DEED</option>
                      <option>OQOOD</option>
                      <option>FORM_A</option>
                      <option>FORM_B</option>
                      <option>FORM_F</option>
                      <option>FORM_I</option>
                      <option>SPA</option>
                      <option>TENANCY_CONTRACT</option>
                      <option>NOC</option>
                      <option>FLOOR_PLAN</option>
                      <option>OWNER_PASSPORT</option>
                      <option>OWNER_EMIRATES_ID</option>
                      <option>OWNER_POA</option>
                      <option>OWNER_COMPANY_DOCUMENT</option>
                      <option>OWNER_BANK_DETAILS</option>
                      <option>COMMISSION_INVOICE</option>
                      <option>COMMISSION_RECEIPT</option>
                      <option>SPLIT_APPROVAL</option>
                      <option>PAYMENT_PROOF</option>
                      <option>OTHER</option>
                    </select>
                  </label>
                  <label>
                    Title
                    <input name="title" required />
                  </label>
                  <label>
                    Use existing uploaded image
                    <select name="mediaAssetId">
                      <option value="">No media link</option>
                      {media.map((x) => (
                        <option value={String(x.id)} key={String(x.id)}>
                          {String(x.title || "Property image")}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    External / DMS reference
                    <input name="externalReference" />
                  </label>
                  <label className="span-2">
                    Remarks
                    <input name="remarks" />
                  </label>
                  <button className="button primary span-2">
                    Link document
                  </button>
                </form>
              </section>
            )}
            {propertyTab === "PORTALS" && (
              <section className="property-tab-panel">
                <h3>Property portal publishing</h3>
                <p className="drawer-note">Validate once, then publish through the approved Bayut XML feed or Property Finder Enterprise connection. Credentials remain encrypted in Integration Centre.</p>
                <div className="portal-publication-list">{((detail.portal_publications || []) as Row[]).map((item) => <article key={String(item.id)}><strong>{String(item.portal).replaceAll("_"," ")}</strong><span className="status">{String(item.status).replaceAll("_"," ")}</span>{Boolean(item.external_listing_id) && <small>Provider listing: {String(item.external_listing_id)}</small>}{Boolean(item.last_success_at) && <small>Last delivered {new Date(String(item.last_success_at)).toLocaleString("en-AE")}</small>}{Number(item.attempt_count || 0) > 0 && <small>{String(item.attempt_count)} delivery attempt{Number(item.attempt_count) === 1 ? "" : "s"}</small>}{Boolean(item.last_error) && <small className="form-error">{String(item.last_error)}</small>}{Array.isArray(item.validation_errors) && item.validation_errors.map((itemError:string) => <small key={itemError}>{itemError}</small>)}</article>)}</div>
                <form className="asset-form compact" onSubmit={publishProperty}>
                  <label>Portal connection<select name="integrationId" required><option value="">Select configured connection…</option>{integrations.map((item) => <option value={String(item.id)} key={String(item.id)}>{String(item.connector).replaceAll("_"," ")} · {String(item.display_name)} · {String(item.status)}</option>)}</select></label>
                  <label>Action<select name="action"><option value="PUBLISH">Publish / refresh</option><option value="WITHDRAW">Withdraw listing</option></select></label>
                  <button className="button primary span-2" disabled={busy}>Submit portal action</button>
                </form>
                {!integrations.length && <div className="empty">Add Bayut or Property Finder in Administration → Integrations, enter the approved provider credentials, and test the connection.</div>}
              </section>
            )}
            {propertyTab === "COMMENTS" && (
              <section className="property-tab-panel">
                <h3>Internal team comments</h3>
                <p className="drawer-note">These comments are never shown on the public website or customer portal.</p>
                {canCommentProperty && <form className="asset-form compact" onSubmit={addPropertyComment}><label className="span-2">Add intelligence or handover note<textarea name="body" required minLength={2} rows={3}/></label><button className="button primary span-2" disabled={busy}>Add internal comment</button></form>}
                <div className="property-comments">{comments.map((comment) => <article key={String(comment.id)}><strong>{String(comment.created_by_name || "Team member")}</strong><p>{String(comment.body)}</p><small>{new Date(String(comment.created_at)).toLocaleString("en-AE")}</small></article>)}{!comments.length && <div className="empty">No internal comments yet.</div>}</div>
              </section>
            )}
          </>
        )}
        {kind === "VEHICLE" && detail && (
          <section className="fleet-lifecycle">
            <div className="fleet-lifecycle-metrics">
              <article>
                <span>Rental history</span>
                <strong>{bookings.length}</strong>
              </article>
              <article>
                <span>Maintenance & events</span>
                <strong>{events.length}</strong>
              </article>
              <article>
                <span>Compliance documents</span>
                <strong>{documents.length}</strong>
              </article>
            </div>
            <details open>
              <summary>Record maintenance, repair or compliance event</summary>
              <form
                className="asset-form lifecycle-form"
                onSubmit={addVehicleEvent}
              >
                <label>
                  Event
                  <select name="eventType">
                    <option>MAINTENANCE</option>
                    <option>SERVICE</option>
                    <option>REPAIR</option>
                    <option>INSPECTION</option>
                    <option>ACCIDENT</option>
                    <option>FINE</option>
                    <option>INSURANCE</option>
                    <option>REGISTRATION</option>
                    <option>CLEANING</option>
                    <option>TYRE_CHANGE</option>
                    <option>OTHER</option>
                  </select>
                </label>
                <label>
                  Date and time
                  <input name="eventAt" type="datetime-local" />
                </label>
                <label>
                  Odometer km
                  <input name="odometerKm" type="number" min="0" />
                </label>
                <label>
                  Fuel %
                  <input name="fuelLevelPct" type="number" min="0" max="100" />
                </label>
                <label>
                  Vendor
                  <input name="vendor" />
                </label>
                <label>
                  Cost
                  <input name="cost" type="number" min="0" step="any" />
                </label>
                <label className="span-2">
                  Notes
                  <textarea name="notes" rows={3} />
                </label>
                <label className="check">
                  <input name="roadworthy" type="checkbox" />
                  Roadworthy check passed
                </label>
                <button className="button primary" disabled={busy}>
                  Record event
                </button>
              </form>
            </details>
            <details>
              <summary>Upload vehicle compliance document</summary>
              <form
                className="asset-form lifecycle-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  const form = new FormData(event.currentTarget),
                    file = form.get("document");
                  if (file instanceof File)
                    void uploadVehicleDocument(
                      file,
                      String(form.get("documentType")),
                      String(form.get("documentNumber") || ""),
                      String(form.get("expiryDate") || ""),
                    );
                }}
              >
                <label>
                  Document type
                  <select name="documentType">
                    <option>REGISTRATION</option>
                    <option>INSURANCE</option>
                    <option>MULKIYA</option>
                    <option>INSPECTION</option>
                    <option>SERVICE_RECORD</option>
                    <option>PURCHASE</option>
                    <option>OTHER</option>
                  </select>
                </label>
                <label>
                  Document number
                  <input name="documentNumber" />
                </label>
                <label>
                  Expiry date
                  <input name="expiryDate" type="date" />
                </label>
                <label>
                  File
                  <input
                    name="document"
                    type="file"
                    required
                    accept="application/pdf,image/jpeg,image/png,image/webp"
                  />
                </label>
                <button className="button primary" disabled={busy}>
                  Upload document
                </button>
              </form>
            </details>
            <div className="lifecycle-timeline">
              {events.slice(0, 12).map((item) => (
                <article key={String(item.id)}>
                  <i />
                  <div>
                    <strong>
                      {String(item.event_type).replaceAll("_", " ")}
                    </strong>
                    <p>
                      {String(
                        item.notes || item.vendor || "Recorded fleet event",
                      )}
                    </p>
                    <small>
                      {new Date(String(item.event_at)).toLocaleString("en-AE")}{" "}
                      · {item.odometer_km ? `${item.odometer_km} km` : ""}{" "}
                      {item.cost ? `· AED ${item.cost}` : ""}
                    </small>
                  </div>
                </article>
              ))}
              {!events.length && (
                <div className="empty">
                  No maintenance or compliance events recorded yet.
                </div>
              )}
            </div>
          </section>
        )}
        {detail && (kind === "VEHICLE" || propertyTab === "EDIT") ? (
          <form className="asset-form" onSubmit={save}>
            {kind === "PROPERTY" ? (
              <>
                <label>
                  Name
                  <input
                    name="titleEn"
                    defaultValue={String(detail.title_en || "")}
                    required
                  />
                </label>
                <label>
                  Category
                  <select
                    name="category"
                    defaultValue={String(detail.category || "RESIDENTIAL")}
                  >
                    <option>RESIDENTIAL</option>
                    <option>COMMERCIAL</option>
                    <option>INDUSTRIAL</option>
                    <option>LAND</option>
                    <option>MIXED_USE</option>
                  </select>
                </label>
                <label>
                  Usage
                  <select
                    name="usageType"
                    defaultValue={String(detail.usage_type || "END_USE")}
                  >
                    <option>END_USE</option>
                    <option>INVESTMENT</option>
                    <option>HOLIDAY_HOME</option>
                    <option>BUSINESS_CENTRE</option>
                  </select>
                </label>
                <label>
                  Listing type
                  <select
                    name="listingType"
                    defaultValue={String(detail.listing_type || "SALE")}
                  >
                    <option>SALE</option>
                    <option>RENT</option>
                    <option>SALE_AND_RENT</option>
                  </select>
                </label>
                <label>
                  Listing source
                  <select
                    name="listingSource"
                    defaultValue={String(detail.listing_source || "")}
                  >
                    <option value="">Select…</option>
                    <option>DIRECT_OWNER</option>
                    <option>DEVELOPER</option>
                    <option>BROKER</option>
                    <option>INTERNAL</option>
                    <option>PORTAL</option>
                  </select>
                </label>
                <label>
                  Unit number
                  <input
                    name="unitNumber"
                    defaultValue={String(detail.unit_number || "")}
                  />
                </label>
                <label>
                  Floor
                  <input
                    name="floorNumber"
                    defaultValue={String(detail.floor_number || "")}
                  />
                </label>
                <label>
                  Type
                  <select
                    name="propertyType"
                    defaultValue={String(detail.property_type || "APARTMENT")}
                  >
                    <option>APARTMENT</option>
                    <option>VILLA</option>
                    <option>OFFICE</option>
                    <option>BUILDING</option>
                    <option>WAREHOUSE</option>
                    <option>LAND</option>
                  </select>
                </label>
                <label>
                  Ownership
                  <select
                    name="ownershipType"
                    defaultValue={String(detail.ownership_type || "CLIENT")}
                  >
                    <option>CLIENT</option>
                    <option>COMPANY</option>
                    <option>DEVELOPER</option>
                  </select>
                </label>
                <label>
                  Availability
                  <select
                    name="availabilityStatus"
                    defaultValue={String(
                      detail.availability_status || "AVAILABLE",
                    )}
                  >
                    <option>AVAILABLE</option>
                    <option>RESERVED</option>
                    <option>UNDER_OFFER</option>
                    <option>SOLD</option>
                    <option>RENTED</option>
                    <option>OFF_MARKET</option>
                  </select>
                </label>
                <label>
                  Emirate
                  <select
                    name="emirate"
                    defaultValue={String(detail.emirate || "Dubai")}
                  >
                    {emirates.map(([value, label]) => (
                      <option value={value} key={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  City
                  <input
                    name="city"
                    defaultValue={String(detail.city || "Dubai")}
                  />
                </label>
                <label>
                  Community
                  <input
                    name="community"
                    defaultValue={String(detail.community || "")}
                  />
                </label>
                <label>
                  Building / project
                  <input
                    name="building"
                    defaultValue={String(detail.building || "")}
                  />
                </label>
                <label>
                  Bedrooms
                  <input
                    name="bedrooms"
                    type="number"
                    min="0"
                    step="0.5"
                    defaultValue={String(detail.bedrooms || "")}
                  />
                </label>
                <label>
                  Bathrooms
                  <input
                    name="bathrooms"
                    type="number"
                    min="0"
                    step="0.5"
                    defaultValue={String(detail.bathrooms || "")}
                  />
                </label>
                <label>
                  Area sqft
                  <input
                    name="areaSqft"
                    type="number"
                    min="0"
                    defaultValue={String(detail.area_sqft || "")}
                  />
                </label>
                <label>
                  Plot sqft
                  <input
                    name="plotSqft"
                    type="number"
                    min="0"
                    defaultValue={String(detail.plot_sqft || "")}
                  />
                </label>
                <label>
                  Asking price
                  <input
                    name="askingPrice"
                    type="number"
                    min="0"
                    defaultValue={String(detail.asking_price || "")}
                  />
                </label>
                <label>
                  Currency
                  <select
                    name="currency"
                    defaultValue={String(detail.currency || "AED")}
                  >
                    <option>AED</option>
                    <option>USD</option>
                    <option>GBP</option>
                    <option>EUR</option>
                  </select>
                </label>
                <label>
                  Furnishing
                  <select
                    name="furnishedStatus"
                    defaultValue={String(detail.furnished_status || "")}
                  >
                    <option value="">Select…</option>
                    <option>UNFURNISHED</option>
                    <option>SEMI_FURNISHED</option>
                    <option>FURNISHED</option>
                  </select>
                </label>
                <label>
                  Parking
                  <input
                    name="parkingCount"
                    type="number"
                    min="0"
                    defaultValue={String(detail.parking_count || "")}
                  />
                </label>
                <label>
                  Developer
                  <input
                    name="developerName"
                    defaultValue={String(detail.developer_name || "")}
                  />
                </label>
                <label>
                  View
                  <input
                    name="viewType"
                    defaultValue={String(detail.view_type || "")}
                  />
                </label>
                <label>
                  Completion
                  <input
                    name="completionStatus"
                    defaultValue={String(detail.completion_status || "")}
                  />
                </label>
                <label>
                  Postal code
                  <input
                    name="postalCode"
                    defaultValue={String(
                      (detail.location_details as Row)?.postalCode || "",
                    )}
                  />
                </label>
                <label>
                  Latitude
                  <input
                    name="latitude"
                    type="number"
                    step="any"
                    defaultValue={String(
                      (detail.location_details as Row)?.latitude || "",
                    )}
                  />
                </label>
                <label>
                  Longitude
                  <input
                    name="longitude"
                    type="number"
                    step="any"
                    defaultValue={String(
                      (detail.location_details as Row)?.longitude || "",
                    )}
                  />
                </label>
                <label>
                  Map URL
                  <input
                    name="mapUrl"
                    type="url"
                    defaultValue={String(
                      (detail.location_details as Row)?.mapUrl || "",
                    )}
                  />
                </label>
                <label>
                  Virtual / 3D tour URL
                  <input
                    name="virtualTourUrl"
                    type="url"
                    defaultValue={String(detail.virtual_tour_url || "")}
                  />
                </label>
                <label>
                  Broker
                  <input
                    name="broker"
                    defaultValue={String(
                      (detail.source_details as Row)?.broker || "",
                    )}
                  />
                </label>
                <label>
                  Broker agent
                  <input
                    name="brokerAgent"
                    defaultValue={String(
                      (detail.source_details as Row)?.brokerAgent || "",
                    )}
                  />
                </label>
                <label>
                  Development
                  <input
                    name="development"
                    defaultValue={String(
                      (detail.source_details as Row)?.development || "",
                    )}
                  />
                </label>
                <label>
                  Market value
                  <input
                    name="marketValue"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)?.marketValue || "",
                    )}
                  />
                </label>
                <label>
                  Sale price / sqft
                  <input
                    name="salePricePerSqft"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)?.salePricePerSqft || "",
                    )}
                  />
                </label>
                <label>
                  Monthly rent
                  <input
                    name="monthlyRent"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)?.monthlyRent || "",
                    )}
                  />
                </label>
                <label>
                  Yearly rent
                  <input
                    name="yearlyRent"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)?.yearlyRent || "",
                    )}
                  />
                </label>
                <label>
                  Rental yield %
                  <input
                    name="rentalYieldPct"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)?.rentalYieldPct || "",
                    )}
                  />
                </label>
                <label>
                  Capital appreciation %
                  <input
                    name="capitalAppreciationPct"
                    type="number"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)
                        ?.capitalAppreciationPct || "",
                    )}
                  />
                </label>
                <label>
                  Service charge
                  <input
                    name="serviceCharge"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)?.serviceCharge || "",
                    )}
                  />
                </label>
                <label>
                  Offered commission %
                  <input
                    name="offeredCommissionPct"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.financial_details as Row)?.offeredCommissionPct ||
                        "",
                    )}
                  />
                </label>
                <label>
                  Purchase price
                  <input
                    name="purchasePrice"
                    type="number"
                    min="0"
                    step=".01"
                    defaultValue={String(
                      (detail.purchase_details as Row)?.purchasePrice || "",
                    )}
                  />
                </label>
                <label>
                  Purchase date
                  <input
                    name="purchaseDate"
                    type="date"
                    defaultValue={String(
                      (detail.purchase_details as Row)?.purchaseDate || "",
                    ).slice(0, 10)}
                  />
                </label>
                <label>
                  Ownership / finance
                  <select
                    name="ownershipFinanceType"
                    defaultValue={String(
                      (detail.purchase_details as Row)?.ownershipFinanceType ||
                        "",
                    )}
                  >
                    <option value="">Select…</option>
                    <option>FULLY_OWNED</option>
                    <option>MORTGAGED</option>
                    <option>BANK_LEASE</option>
                    <option>PAYMENT_PLAN</option>
                  </select>
                </label>
                <label>
                  Balconies
                  <input
                    name="balconies"
                    type="number"
                    min="0"
                    defaultValue={String(
                      (detail.additional_details as Row)?.balconies || "",
                    )}
                  />
                </label>
                <label>
                  Maid room
                  <select
                    name="maidRoom"
                    defaultValue={
                      (detail.additional_details as Row)?.maidRoom
                        ? "YES"
                        : "NO"
                    }
                  >
                    <option>NO</option>
                    <option>YES</option>
                  </select>
                </label>
                <label>
                  Study
                  <select
                    name="study"
                    defaultValue={
                      (detail.additional_details as Row)?.study ? "YES" : "NO"
                    }
                  >
                    <option>NO</option>
                    <option>YES</option>
                  </select>
                </label>
                <label>
                  Vacant on transfer
                  <select
                    name="vacantOnTransfer"
                    defaultValue={
                      (detail.additional_details as Row)?.vacantOnTransfer
                        ? "YES"
                        : "NO"
                    }
                  >
                    <option>NO</option>
                    <option>YES</option>
                  </select>
                </label>
                <label>
                  Handover date
                  <input
                    name="handoverDate"
                    type="date"
                    defaultValue={String(
                      (detail.additional_details as Row)?.handoverDate || "",
                    ).slice(0, 10)}
                  />
                </label>
                <label>
                  Title deed / Oqood no.
                  <input
                    name="titleDeedNumber"
                    defaultValue={String(
                      (detail.additional_details as Row)?.titleDeedNumber || "",
                    )}
                  />
                </label>
                <label>
                  RERA permit no.
                  <input
                    name="permitNumber"
                    defaultValue={String(
                      (detail.additional_details as Row)?.permitNumber || "",
                    )}
                  />
                </label>
                <label className="span-2">
                  Tax implications
                  <textarea
                    name="taxImplications"
                    defaultValue={String(
                      (detail.financial_details as Row)?.taxImplications || "",
                    )}
                  />
                </label>
                <label className="span-2">
                  Purchase remarks
                  <textarea
                    name="purchaseRemarks"
                    defaultValue={String(
                      (detail.purchase_details as Row)?.purchaseRemarks || "",
                    )}
                  />
                </label>
                <label className="span-2">
                  Room details JSON
                  <textarea
                    name="roomDetailsJson"
                    defaultValue={JSON.stringify(
                      detail.room_details || [],
                      null,
                      2,
                    )}
                    rows={6}
                  />
                  <small>
                    Record room name, type, sizeSqft and ensuite for each room.
                  </small>
                </label>
                <label className="span-2">
                  Address
                  <textarea
                    name="addressEn"
                    defaultValue={String(detail.address_en || "")}
                  />
                </label>
                <label className="span-2">
                  Description
                  <textarea
                    name="descriptionEn"
                    defaultValue={String(detail.description_en || "")}
                  />
                </label>
              </>
            ) : (
              <>
                <label>
                  Status
                  <select
                    name="status"
                    defaultValue={String(detail.status || "ACTIVE")}
                  >
                    <option>ACTIVE</option>
                    <option>RENTED</option>
                    <option>RESERVED</option>
                    <option>MAINTENANCE</option>
                    <option>UNAVAILABLE</option>
                    <option>SOLD</option>
                    <option>INACTIVE</option>
                  </select>
                </label>
                <label>
                  Odometer km
                  <input
                    name="odometerKm"
                    type="number"
                    min="0"
                    defaultValue={String(detail.odometer_km || "")}
                  />
                </label>
                <label>
                  Body type
                  <select
                    name="bodyType"
                    defaultValue={String(detail.body_type || "")}
                  >
                    <option value="">Select…</option>
                    <option>SEDAN</option>
                    <option>SUV</option>
                    <option>COUPE</option>
                    <option>CONVERTIBLE</option>
                    <option>HATCHBACK</option>
                    <option>VAN</option>
                    <option>PICKUP</option>
                    <option>LUXURY</option>
                  </select>
                </label>
                <label>
                  Trim / specification
                  <input
                    name="trimLevel"
                    defaultValue={String(detail.trim_level || "")}
                  />
                </label>
                <label>
                  Engine number
                  <input
                    name="engineNumber"
                    defaultValue={String(detail.engine_number || "")}
                  />
                </label>
                <label>
                  Engine size
                  <input
                    name="engineSize"
                    defaultValue={String(detail.engine_size || "")}
                  />
                </label>
                <label>
                  Drive type
                  <select
                    name="driveType"
                    defaultValue={String(detail.drive_type || "")}
                  >
                    <option value="">Select…</option>
                    <option>FWD</option>
                    <option>RWD</option>
                    <option>AWD</option>
                    <option>4WD</option>
                  </select>
                </label>
                <label>
                  Keys held
                  <input
                    name="keysCount"
                    type="number"
                    min="0"
                    max="10"
                    defaultValue={String(detail.keys_count ?? 2)}
                  />
                </label>
                <label>
                  Ownership
                  <select
                    name="ownershipStatus"
                    defaultValue={String(detail.ownership_status || "OWNED")}
                  >
                    <option>OWNED</option>
                    <option>FINANCED</option>
                    <option>LEASED</option>
                  </select>
                </label>
                <label>
                  Acquisition date
                  <input
                    name="acquisitionDate"
                    type="date"
                    defaultValue={String(detail.acquisition_date || "").slice(
                      0,
                      10,
                    )}
                  />
                </label>
                <label>
                  Warranty expiry
                  <input
                    name="warrantyExpiry"
                    type="date"
                    defaultValue={String(detail.warranty_expiry || "").slice(
                      0,
                      10,
                    )}
                  />
                </label>
                <label>
                  Transmission
                  <select
                    name="transmission"
                    defaultValue={String(detail.transmission || "")}
                  >
                    <option value="">Select…</option>
                    <option>AUTOMATIC</option>
                    <option>MANUAL</option>
                  </select>
                </label>
                <label>
                  Fuel
                  <select
                    name="fuelType"
                    defaultValue={String(detail.fuel_type || "")}
                  >
                    <option value="">Select…</option>
                    <option>PETROL</option>
                    <option>DIESEL</option>
                    <option>HYBRID</option>
                    <option>ELECTRIC</option>
                  </select>
                </label>
                <label>
                  Seats
                  <input
                    name="seats"
                    type="number"
                    min="1"
                    defaultValue={String(detail.seats || "")}
                  />
                </label>
                <label>
                  Doors
                  <input
                    name="doors"
                    type="number"
                    min="1"
                    defaultValue={String(detail.doors || "")}
                  />
                </label>
                <label>
                  Daily rate
                  <input
                    name="dailyRate"
                    type="number"
                    min="0"
                    defaultValue={String(detail.daily_rate || "")}
                  />
                </label>
                <label>
                  Weekly rate
                  <input
                    name="weeklyRate"
                    type="number"
                    min="0"
                    defaultValue={String(detail.weekly_rate || "")}
                  />
                </label>
                <label>
                  Monthly rate
                  <input
                    name="monthlyRate"
                    type="number"
                    min="0"
                    defaultValue={String(detail.monthly_rate || "")}
                  />
                </label>
                <label>
                  Deposit
                  <input
                    name="depositAmount"
                    type="number"
                    min="0"
                    defaultValue={String(detail.deposit_amount || "")}
                  />
                </label>
                <label>
                  Registration expiry
                  <input
                    name="registrationExpiry"
                    type="date"
                    defaultValue={String(
                      detail.registration_expiry || "",
                    ).slice(0, 10)}
                  />
                </label>
                <label>
                  Insurance expiry
                  <input
                    name="insuranceExpiry"
                    type="date"
                    defaultValue={String(detail.insurance_expiry || "").slice(
                      0,
                      10,
                    )}
                  />
                </label>
                <label className="span-2">
                  Description
                  <textarea
                    name="description"
                    defaultValue={String(detail.description || "")}
                  />
                </label>
              </>
            )}
            <label className="span-2">
              Features (comma separated)
              <input name="features" defaultValue={features} />
            </label>
            <label className="check span-2">
              <input
                name="listedPublicly"
                type="checkbox"
                defaultChecked={Boolean(detail.listed_publicly)}
              />
              Publish on public website
            </label>
            <button className="button primary span-2" disabled={busy}>
              <Save size={17} />
              {busy ? "Saving…" : "Save changes"}
            </button>
          </form>
        ) : !detail ? (
          <div className="empty">Loading details…</div>
        ) : null}
      </aside>
    </div>
  );
}

function ReportsWorkspace() {
  const [summary, setSummary] = useState<Record<string, any> | null>(null);
  const [saved, setSaved] = useState<Row[]>([]);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([
      api<Record<string, any>>("/api/v1/reports/executive-summary"),
      api<{ data: Row[] }>("/api/v1/reports/saved"),
    ])
      .then(([a, b]) => {
        setSummary(a);
        setSaved(b.data);
      })
      .catch((value) =>
        setError(
          value instanceof Error ? value.message : "Unable to load reports",
        ),
      );
  }, []);
  const money = (value: unknown) =>
    new Intl.NumberFormat("en-AE", {
      style: "currency",
      currency: "AED",
      maximumFractionDigits: 0,
    }).format(Number(value || 0));
  const sales = (summary?.salesByBusiness || []) as Row[];
  return (
    <div className="content">
      <section className="page-heading">
        <div>
          <span className="kicker">Analytics & statutory outputs</span>
          <h1>Reports & Analytics</h1>
          <p>
            Live dashboards, saved filters, drill-down, scheduled PDF/Excel
            delivery, board packs and UAE statutory reporting.
          </p>
        </div>
        <button className="button primary">
          <Plus size={17} />
          Build report
        </button>
      </section>
      {error && <div className="form-error">{error}</div>}
      <section className="metrics">
        <article>
          <span>Sales YTD</span>
          <strong>
            {money(sales.reduce((sum, row) => sum + Number(row.sales || 0), 0))}
          </strong>
          <small>Issued invoices less credits</small>
        </article>
        <article>
          <span>Receivables</span>
          <strong>{money(summary?.receivables?.total)}</strong>
          <small>
            {summary?.receivables?.overdue_count || 0} overdue invoices
          </small>
        </article>
        <article>
          <span>Approvals</span>
          <strong>{summary?.approvals?.pending || 0}</strong>
          <small>{money(summary?.approvals?.value)} pending</small>
        </article>
        <article>
          <span>PDC due in 30 days</span>
          <strong>{summary?.cheques?.due_30 || 0}</strong>
          <small>{money(summary?.cheques?.value)} held</small>
        </article>
      </section>
      <section className="work-grid">
        <article className="panel wide">
          <header>
            <div>
              <span className="kicker">Group sales</span>
              <h2>Year-to-date by business</h2>
            </div>
          </header>
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Business</th>
                  <th>Sales</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((row) => (
                  <tr key={String(row.id)}>
                    <td>{String(row.business)}</td>
                    <td>{money(row.sales)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!sales.length && (
              <div className="empty">
                No issued invoices in this reporting period.
              </div>
            )}
          </div>
        </article>
        <article className="panel task-panel">
          <header>
            <div>
              <span className="kicker">Operational risk</span>
              <h2>Control indicators</h2>
            </div>
          </header>
          <ul>
            <li>
              <button>
                <span className="task-icon urgent">
                  <AlertTriangle size={16} />
                </span>
                <span>
                  Fleet expiry risk
                  <small>
                    {summary?.fleet?.expiry_risk || 0} within 30 days
                  </small>
                </span>
              </button>
            </li>
            <li>
              <button>
                <span className="task-icon">
                  <CalendarClock size={16} />
                </span>
                <span>
                  Occupancy
                  <small>
                    {summary?.occupancy?.occupied || 0} of{" "}
                    {summary?.occupancy?.total || 0} units
                  </small>
                </span>
              </button>
            </li>
          </ul>
        </article>
      </section>
      <section className="report-catalog">
        <article>
          <CircleDollarSign />
          <div>
            <h2>Finance & UAE VAT</h2>
            <p>
              Trial balance, P&amp;L, balance sheet, cash flow, ageing, VAT
              return and audit trail.
            </p>
          </div>
        </article>
        <article>
          <CalendarClock />
          <div>
            <h2>Operational performance</h2>
            <p>
              Lead SLA, occupancy, fleet utilisation, work-order delivery and
              employee production.
            </p>
          </div>
        </article>
        <article>
          <AlertTriangle />
          <div>
            <h2>Saved report library</h2>
            <p>
              {saved.length} role-controlled definitions ready for scheduled
              PDF, Excel or CSV delivery.
            </p>
          </div>
        </article>
      </section>
    </div>
  );
}
